import React from 'react';
import moment from 'moment';
import {Button} from 'reactstrap';
import Cookies from 'js-cookie';

import API from '@beardeddevops/react.api';
import APPCONFIG from 'constants/Config';
import Framework from '@beardeddevops/react.framework';
import Types from 'constants/Types';

import './RangeManager.scss';

const breadcrumbsStatic = [
	{
		name: 'Range Manager',
		url: '/range-manager',
		icon: 'dashboard',
	},
];

const defaultSettings = {
	dashboard_name: 'Range Manager',
	default_unit_minutes: 30,
	minimum_units: 1,
	grace_minutes: 5,
	allow_lane_extensions: true,
	active: true,
};

const defaultRatePlan = {
	name: 'Standard Lane Time',
	pricing_type: 'per_lane',
	base_unit_minutes: 30,
	unit_minutes: 10,
	minimum_units: 1,
	base_price: 0,
	additional_unit_minutes: 10,
	additional_unit_price: 0,
	maximum_additional_units: '',
	is_default: true,
	active: true,
	sort_order: 0,
	notes: '',
};

const customerSelectProperty = {
	class: Types.Customers._name,
	error: '',
	label: 'Existing Customer',
	name: 'customer',
	reference: 'customers',
};

const registerSelectProperty = {
	class: Types.Registers._name,
	error: '',
	label: 'Register',
	name: 'register',
	reference: 'registers',
};

const terminalSelectProperty = {
	class: Types.Terminals._name,
	error: '',
	label: 'Terminal',
	name: 'terminal',
	reference: 'terminals',
};

const rangeToastOptions = {
	position: API.Toast.POSITION && API.Toast.POSITION.TOP_CENTER ? API.Toast.POSITION.TOP_CENTER : 'top-center',
};

class RangeManager extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			activeView: 'live',
			loading: true,
			dashboardLoaded: false,
			refreshing: false,
			saving: false,
			deletingRatePlan: null,
			configurationDirty: false,
			laneCountDirty: false,
			paymentProcessing: false,
			paymentAbortProcessing: false,
			paymentStatusMessage: '',
			activePaymentReference: '',
			activePaymentTerminal: '',
			currentMinute: moment()
				.format('YYYY-MM-DDTHH:mm'),
			calendarDate: moment()
				.format('YYYY-MM-DD'),
			calendarViewMode: 'day',
			calendarDataKey: '',
			calendarDataStart: '',
			calendarDataEnd: '',
			calendarExpandedDay: '',
			blockSaving: false,
			settings: {...defaultSettings},
			lanes: [],
			ratePlans: [],
			bookings: [],
			waitlist: [],
			blocks: [],
			summary: {},
			bookingForm: this.emptyBookingForm(),
			bookingRangeMinutes: this.emptyBookingRangeMinutes(),
			waitlistForm: this.emptyWaitlistForm(),
			blockForm: this.emptyBlockForm(),
			bookingPanelOpen: true,
			waitlistPanelOpen: false,
			promoteLaneByEntry: {},
			laneCountTarget: 0,
		};

		this.dashboardRequestId = 0;
		this.configurationRevision = 0;
		this.paymentAbortRequested = false;
		this.calendarDayDetailsRef = React.createRef();
		this.blockPanelRef = React.createRef();
	}

	componentDidMount() {
		this.loadDashboard();
		this.startMinuteClock();
		this.refreshTimer = setInterval(() => this.loadDashboard(), 60000);
	}

	componentWillUnmount() {
		if (this.refreshTimer) {
			clearInterval(this.refreshTimer);
		}

		if (this.minuteTimer) {
			clearTimeout(this.minuteTimer);
		}
	}

	emptyBookingForm = () => ({
		customer: '',
		customers: null,
		customer_mode: 'existing',
		customer_name: '',
		customer_phone: '',
		customer_email: '',
		lane: '',
		party_size: 1,
		additional_units: 0,
		reserved_units: 1,
		rate_plan: '',
		payment_method: 'ipospays',
		charge_with_ipospays: true,
		include_credit_card_fee: true,
		use_range_minutes: true,
		register: Cookies.get('range_manager_register') || '',
		terminal: Cookies.get('range_manager_terminal') || '',
		start_mode: 'now',
		scheduled_start: moment()
			.format('YYYY-MM-DDTHH:mm'),
		notes: '',
	});

	emptyBookingRangeMinutes = () => ({
		customer: '',
		loading: false,
		available_minutes: 0,
		memberships: [],
		error: '',
	});

	emptyWaitlistForm = () => ({
		customer: '',
		customers: null,
		customer_mode: 'existing',
		customer_name: '',
		customer_phone: '',
		customer_email: '',
		party_size: 1,
		requested_minutes: 30,
		requested_lane_type: '',
		requested_rate_plan: '',
		priority: 0,
		notes: '',
	});

	emptyBlockForm = () => ({
		pkey: '',
		lane: '',
		title: '',
		block_type: 'maintenance',
		starts_at: moment()
			.format('YYYY-MM-DDTHH:mm'),
		ends_at: moment()
			.add(1, 'hour')
			.format('YYYY-MM-DDTHH:mm'),
		notes: '',
	});

	apiRequest = async (type, path, object = {}) => {
		let result;
		try {
			result = await $.ajax({
				type,
				url: `${APPCONFIG.settings.apiURL()}/${APPCONFIG.settings.apiVersion}${path}`,
				data: {
					object: JSON.stringify(object),
				},
				// Range Manager catches request failures and shows its own toast.
				error: () => {},
			});
		} catch (error) {
			let message = error && error.responseJSON ? (error.responseJSON.response || error.responseJSON.message) : null;
			if (!message && error && error.responseText) {
				try {
					const responseBody = JSON.parse(error.responseText);
					message = responseBody.response || responseBody.message;
				} catch (parseError) {
					message = error.responseText;
				}
			}
			if (message && typeof message !== 'string') {
				message = JSON.stringify(message);
			}
			throw new Error(message || 'Range Manager request failed.');
		}

		return result && typeof result.response !== 'undefined' ? result.response : result;
	};

	loadBookingRangeMinutes = async (customer) => {
		if (!customer) {
			this.setState({bookingRangeMinutes: this.emptyBookingRangeMinutes()});
			return;
		}

		const selectedCustomer = `${customer}`;
		this.setState({
			bookingRangeMinutes: {
				...this.state.bookingRangeMinutes,
				customer: selectedCustomer,
				loading: true,
				error: '',
			},
		});

		try {
			const response = await this.apiRequest('GET', '/range_manager/customer_range_minutes', {
				customer,
				booking_start: this.state.bookingForm.start_mode === 'scheduled'
					? this.state.bookingForm.scheduled_start
					: moment().format('YYYY-MM-DDTHH:mm'),
			});
			if (`${this.state.bookingForm.customer}` !== selectedCustomer) {
				return;
			}

			this.setState({
				bookingRangeMinutes: {
					customer: selectedCustomer,
					loading: false,
					available_minutes: parseInt(response && response.available_minutes, 10) || 0,
					memberships: response && Array.isArray(response.memberships) ? response.memberships : [],
					error: '',
				},
			});
		} catch (error) {
			if (`${this.state.bookingForm.customer}` !== selectedCustomer) {
				return;
			}

			this.setState({
				bookingRangeMinutes: {
					customer: selectedCustomer,
					loading: false,
					available_minutes: 0,
					memberships: [],
					error: error.message || 'Unable to load membership range minutes.',
				},
			});
		}
	};

	createPaymentReference = () => `range${Date.now()}${Math.random().toString(36).slice(2, 10)}`;

	startTerminalPaymentState = (paymentPayload, paymentStatusMessage) => {
		const paymentReference = this.createPaymentReference();
		this.paymentAbortRequested = false;
		return new Promise((resolve) => {
			this.setState({
				paymentProcessing: true,
				paymentAbortProcessing: false,
				paymentStatusMessage,
				activePaymentReference: paymentReference,
				activePaymentTerminal: paymentPayload.terminal,
			}, () => {
				const submitPaymentPayload = {
					...paymentPayload,
					payment_reference_id: paymentReference,
				};
				const resolveAfterPaint = () => setTimeout(() => resolve(submitPaymentPayload), 0);

				if (window.requestAnimationFrame) {
					window.requestAnimationFrame(resolveAfterPaint);
					return;
				}

				resolveAfterPaint();
			});
		});
	};

	clearTerminalPaymentState = () => {
		this.setState({
			paymentProcessing: false,
			paymentAbortProcessing: false,
			paymentStatusMessage: '',
			activePaymentReference: '',
			activePaymentTerminal: '',
		});
	};

	abortTerminalPayment = async () => {
		if (!this.state.activePaymentReference || !this.state.activePaymentTerminal) {
			API.Toast.error('No active terminal charge was found to abort.', rangeToastOptions);
			return;
		}

		this.paymentAbortRequested = true;
		this.setState({
			paymentAbortProcessing: true,
			paymentStatusMessage: 'Sending abort request to the terminal...',
		});

		try {
			await this.apiRequest('POST', '/range_manager/abort', {
				terminal: this.state.activePaymentTerminal,
				payment_reference_id: this.state.activePaymentReference,
			});
			API.Toast.success('Terminal abort request sent.', rangeToastOptions);
			this.setState({
				paymentStatusMessage: 'Abort request sent. Waiting for the terminal response.',
			});
		} catch (error) {
			API.Toast.error(error.message || 'Unable to abort the terminal charge.', rangeToastOptions);
		} finally {
			this.setState({paymentAbortProcessing: false});
		}
	};

	startMinuteClock = () => {
		const scheduleNextTick = () => {
			const now = new Date();
			const delay = 60000 - ((now.getSeconds() * 1000) + now.getMilliseconds());

			this.minuteTimer = setTimeout(() => {
				this.setState({
					currentMinute: moment()
						.format('YYYY-MM-DDTHH:mm'),
				});
				scheduleNextTick();
			}, delay || 60000);
		};

		scheduleNextTick();
	};

	getCurrentMoment = () => moment(this.state.currentMinute || undefined);

	getDashboardDataState = (data, previousState, calendarQuery, options = {}) => {
		const preserveConfiguration = previousState.dashboardLoaded && previousState.configurationDirty && !options.replaceConfiguration;
		const settings = preserveConfiguration ? previousState.settings : {...defaultSettings, ...(data.settings || {})};
		const lanes = preserveConfiguration ? previousState.lanes : (data.lanes || []);
		const ratePlans = preserveConfiguration ? previousState.ratePlans : (data.rate_plans && data.rate_plans.length ? data.rate_plans : [{...defaultRatePlan}]);
		const defaultPlan = this.getDefaultRatePlanFromList(ratePlans);
		const calendarDataKey = calendarQuery ? calendarQuery.key : previousState.calendarDataKey;

		return {
			settings,
			lanes,
			ratePlans,
			bookings: data.bookings || [],
			waitlist: data.waitlist || [],
			blocks: data.blocks || [],
			summary: data.summary || {},
			calendarDataKey,
			calendarDataStart: calendarQuery ? calendarQuery.dateStart : previousState.calendarDataStart,
			calendarDataEnd: calendarQuery ? calendarQuery.dateEnd : previousState.calendarDataEnd,
			laneCountTarget: preserveConfiguration || previousState.activeView === 'configuration' ? previousState.laneCountTarget : lanes.length,
			bookingForm: {
				...previousState.bookingForm,
				reserved_units: previousState.bookingForm.reserved_units || settings.minimum_units,
				rate_plan: previousState.bookingForm.rate_plan || defaultPlan.pkey || '',
			},
			waitlistForm: {
				...previousState.waitlistForm,
				requested_minutes: previousState.waitlistForm.requested_minutes || settings.default_unit_minutes,
				requested_rate_plan: previousState.waitlistForm.requested_rate_plan || defaultPlan.pkey || '',
			},
		};
	};

	getDashboardSnapshot = (state) => JSON.stringify({
		settings: state.settings,
		lanes: state.lanes,
		ratePlans: state.ratePlans,
		bookings: state.bookings,
		waitlist: state.waitlist,
		blocks: state.blocks,
		summary: state.summary,
		calendarDataKey: state.calendarDataKey,
		calendarDataStart: state.calendarDataStart,
		calendarDataEnd: state.calendarDataEnd,
		calendarExpandedDay: state.calendarExpandedDay,
		laneCountTarget: state.laneCountTarget,
		bookingForm: state.bookingForm,
		waitlistForm: state.waitlistForm,
	});

	loadDashboard = async (options = {}) => {
		const requestOptions = options && options.preventDefault ? {} : options;
		const calendarQuery = this.getCalendarQuery();
		const requestId = this.dashboardRequestId + 1;
		this.dashboardRequestId = requestId;

		const loadState = {};
		if (!this.state.dashboardLoaded) {
			loadState.loading = true;
		} else if (requestOptions.showRefreshing && !this.state.refreshing) {
			loadState.refreshing = true;
		}
		if (Object.keys(loadState).length) {
			this.setState(loadState);
		}

		try {
			const data = await this.apiRequest('GET', '/range_manager/dashboard', {
				date: calendarQuery.date,
				date_start: calendarQuery.dateStart,
				date_end: calendarQuery.dateEnd,
				calendar_view: calendarQuery.viewMode,
			});

			if (requestId !== this.dashboardRequestId) {
				return;
			}

			this.setState((previousState) => {
				const replaceConfiguration = requestOptions.replaceConfiguration &&
					(typeof requestOptions.configurationRevision !== 'number' || requestOptions.configurationRevision === this.configurationRevision);
				const dashboardState = this.getDashboardDataState(data || {}, previousState, calendarQuery, {
					...requestOptions,
					replaceConfiguration,
				});
				const nextState = {...previousState, ...dashboardState};

				if (previousState.dashboardLoaded && this.getDashboardSnapshot(previousState) === this.getDashboardSnapshot(nextState)) {
					if (previousState.loading || previousState.refreshing || !previousState.dashboardLoaded) {
						return {loading: false, dashboardLoaded: true, refreshing: false};
					}

					return null;
				}

				return {
					loading: false,
					dashboardLoaded: true,
					refreshing: false,
					...dashboardState,
				};
			});
		} catch (error) {
			if (requestId !== this.dashboardRequestId) {
				return;
			}

			this.setState((previousState) => ({
				loading: false,
				refreshing: false,
			}));
			API.Toast.error(error.message || 'Unable to load Range Manager.', rangeToastOptions);
		}
	};

	handleManualRefresh = () => {
		this.loadDashboard({showRefreshing: true});
	};

	togglePanel = (panelStateKey) => {
		this.setState((previousState) => ({
			[panelStateKey]: !previousState[panelStateKey],
		}));
	};

	setActiveView = (activeView) => {
		this.setState({activeView}, () => {
			const calendarData = this.getVisibleCalendarData();
			if (activeView === 'calendar' && !calendarData.hasCurrentCalendarData) {
				this.loadDashboard();
			}
		});
	};

	updateForm = (formName, field, value) => {
		this.setState({
			[formName]: {
				...this.state[formName],
				[field]: value,
			},
		}, () => {
			if (formName === 'bookingForm' && (field === 'scheduled_start' || field === 'start_mode') && this.state.bookingForm.customer) {
				this.loadBookingRangeMinutes(this.state.bookingForm.customer);
			}
		});
	};

	setBookingStartMode = (startMode) => {
		this.setState((previousState) => ({
			bookingForm: {
				...previousState.bookingForm,
				start_mode: startMode === 'scheduled' ? 'scheduled' : 'now',
				scheduled_start: previousState.bookingForm.scheduled_start || moment()
					.format('YYYY-MM-DDTHH:mm'),
			},
		}));
	};

	setBookingCustomerMode = (customerMode) => {
		this.setState((previousState) => ({
			bookingForm: {
				...previousState.bookingForm,
				customer_mode: customerMode,
				customer: customerMode === 'new' ? '' : previousState.bookingForm.customer,
				customers: customerMode === 'new' ? null : previousState.bookingForm.customers,
				customer_name: customerMode === 'new' ? '' : previousState.bookingForm.customer_name,
				customer_phone: customerMode === 'new' ? '' : previousState.bookingForm.customer_phone,
				customer_email: customerMode === 'new' ? '' : previousState.bookingForm.customer_email,
				use_range_minutes: customerMode === 'new' ? false : previousState.bookingForm.use_range_minutes,
			},
			bookingRangeMinutes: customerMode === 'new' ? this.emptyBookingRangeMinutes() : previousState.bookingRangeMinutes,
		}), () => {
			if (customerMode !== 'new' && this.state.bookingForm.customer) {
				this.loadBookingRangeMinutes(this.state.bookingForm.customer);
			}
		});
	};

	updateBookingCustomer = (name, pkey, namePlural, customer) => {
		this.setState((previousState) => ({
			bookingForm: {
				...previousState.bookingForm,
				customer: pkey || '',
				customers: customer || null,
				customer_name: customer && customer.name ? customer.name : '',
				customer_phone: customer && customer.phone ? this.formatPhoneNumber(customer.phone) : '',
				customer_email: customer && customer.email ? customer.email : '',
				use_range_minutes: true,
			},
			bookingRangeMinutes: pkey ? {
				...previousState.bookingRangeMinutes,
				customer: `${pkey}`,
				loading: true,
				error: '',
			} : this.emptyBookingRangeMinutes(),
		}), () => this.loadBookingRangeMinutes(pkey || ''));
	};

	updateNewBookingCustomer = (field, value) => {
		const nextValue = field === 'customer_phone' ? this.formatPhoneNumber(value) : value;
		this.setState((previousState) => ({
			bookingForm: {
				...previousState.bookingForm,
				customer: previousState.bookingForm.customer_mode === 'new' ? '' : previousState.bookingForm.customer,
				customers: previousState.bookingForm.customer_mode === 'new' ? null : previousState.bookingForm.customers,
				use_range_minutes: previousState.bookingForm.customer_mode === 'new' ? false : previousState.bookingForm.use_range_minutes,
				[field]: nextValue,
			},
			bookingRangeMinutes: previousState.bookingForm.customer_mode === 'new' ? this.emptyBookingRangeMinutes() : previousState.bookingRangeMinutes,
		}));
	};

	setWaitlistCustomerMode = (customerMode) => {
		this.setState((previousState) => ({
			waitlistForm: {
				...previousState.waitlistForm,
				customer_mode: customerMode,
				customer: customerMode === 'new' ? '' : previousState.waitlistForm.customer,
				customers: customerMode === 'new' ? null : previousState.waitlistForm.customers,
				customer_name: customerMode === 'new' ? '' : previousState.waitlistForm.customer_name,
				customer_phone: customerMode === 'new' ? '' : previousState.waitlistForm.customer_phone,
				customer_email: customerMode === 'new' ? '' : previousState.waitlistForm.customer_email,
			},
		}));
	};

	updateWaitlistCustomer = (name, pkey, namePlural, customer) => {
		this.setState((previousState) => ({
			waitlistForm: {
				...previousState.waitlistForm,
				customer: pkey || '',
				customers: customer || null,
				customer_name: customer && customer.name ? customer.name : '',
				customer_phone: customer && customer.phone ? this.formatPhoneNumber(customer.phone) : '',
				customer_email: customer && customer.email ? customer.email : '',
			},
		}));
	};

	updateNewWaitlistCustomer = (field, value) => {
		const nextValue = field === 'customer_phone' ? this.formatPhoneNumber(value) : value;
		this.setState((previousState) => ({
			waitlistForm: {
				...previousState.waitlistForm,
				customer: previousState.waitlistForm.customer_mode === 'new' ? '' : previousState.waitlistForm.customer,
				customers: previousState.waitlistForm.customer_mode === 'new' ? null : previousState.waitlistForm.customers,
				[field]: nextValue,
			},
		}));
	};

	updateBookingPaymentSelect = (name, pkey) => {
		if (name === 'register') {
			if (pkey) {
				Cookies.set('range_manager_register', pkey);
			} else {
				Cookies.remove('range_manager_register');
			}
		}

		if (name === 'terminal') {
			if (pkey) {
				Cookies.set('range_manager_terminal', pkey);
			} else {
				Cookies.remove('range_manager_terminal');
			}
		}

		this.updateForm('bookingForm', name, pkey || '');
	};

	updateSetting = (field, value) => {
		this.configurationRevision += 1;
		this.setState((previousState) => ({
			settings: {
				...previousState.settings,
				[field]: value,
			},
			configurationDirty: true,
		}));
	};

	updateLane = (index, field, value) => {
		this.configurationRevision += 1;
		const lanes = [...this.state.lanes];
		lanes[index] = {
			...lanes[index],
			[field]: value,
		};
		this.setState({lanes, configurationDirty: true});
	};

	deleteLane = async (lane, index) => {
		if (!window.confirm(`Delete ${lane.name || 'this lane'}? This cannot be undone.`)) {
			return;
		}

		if (!lane.pkey) {
			this.configurationRevision += 1;
			const lanes = this.state.lanes.filter((rangeLane, laneIndex) => laneIndex !== index);
			this.setState({
				lanes,
				laneCountTarget: lanes.length,
				laneCountDirty: false,
				configurationDirty: true,
			});
			return;
		}

		try {
			await this.apiRequest('DELETE', '/range_manager/lane', {
				pkey: lane.pkey,
			});
			const lanes = this.state.lanes.filter((rangeLane, laneIndex) => laneIndex !== index);
			API.Toast.success('Range lane deleted.', rangeToastOptions);
			this.setState({
				lanes,
				laneCountTarget: lanes.length,
				laneCountDirty: false,
			});
		} catch (error) {
			API.Toast.error(error.message || 'Unable to delete range lane.', rangeToastOptions);
		}
	};

	updateRatePlan = (index, field, value) => {
		this.configurationRevision += 1;
		let ratePlans = [...this.state.ratePlans];
		const nextRatePlan = {
			...ratePlans[index],
			[field]: value,
		};

		if (field === 'additional_unit_minutes') {
			nextRatePlan.unit_minutes = value;
		}

		if (field === 'base_unit_minutes') {
			nextRatePlan.minimum_units = 1;
		}

		ratePlans[index] = {
			...nextRatePlan,
		};

		if (field === 'is_default' && value) {
			ratePlans = ratePlans.map((ratePlan, ratePlanIndex) => ({
				...ratePlan,
				is_default: ratePlanIndex === index,
			}));
		}

		const selectedDefaultPlan = field === 'is_default' && value ? ratePlans[index] : null;
		const nextState = {
			ratePlans,
			configurationDirty: true,
		};

		if (selectedDefaultPlan) {
			nextState.bookingForm = {
				...this.state.bookingForm,
				rate_plan: selectedDefaultPlan.pkey || '',
			};
			nextState.waitlistForm = {
				...this.state.waitlistForm,
				requested_rate_plan: selectedDefaultPlan.pkey || '',
			};
		}

		this.setState(nextState);
	};

	applyLaneCount = () => {
		this.configurationRevision += 1;
		const target = Math.max(parseInt(this.state.laneCountTarget, 10) || 0, 0);
		const lanes = [...this.state.lanes];

		while (lanes.length < target) {
			const nextNumber = lanes.length + 1;
			lanes.push({
				name: `Lane ${nextNumber}`,
				lane_number: nextNumber,
				lane_type: 'Standard',
				capacity: 1,
				status: 'active',
				display_order: nextNumber - 1,
				notes: '',
			});
		}

		for (let index = target; index < lanes.length; index++) {
			lanes[index] = {
				...lanes[index],
				status: 'inactive',
			};
		}

		this.setState({
			lanes,
			configurationDirty: true,
			laneCountDirty: false,
		});
	};

	addRatePlan = () => {
		this.configurationRevision += 1;
		this.setState({
			ratePlans: [
				...this.state.ratePlans,
				{
					...defaultRatePlan,
					name: `Rate Plan ${this.state.ratePlans.length + 1}`,
					is_default: this.state.ratePlans.length === 0,
					sort_order: this.state.ratePlans.length,
				},
			],
			configurationDirty: true,
		});
	};

	deleteRatePlan = async (ratePlan, index) => {
		if (this.state.ratePlans.length <= 1) {
			return;
		}

		if (!window.confirm(`Delete ${ratePlan.name || 'this rate plan'}? This cannot be undone.`)) {
			return;
		}

		const ratePlanKey = ratePlan.pkey || `new-${index}`;
		if (ratePlan.pkey) {
			this.setState({deletingRatePlan: ratePlanKey});
			try {
				await this.apiRequest('DELETE', '/range_manager/rate_plan', {pkey: ratePlan.pkey});
				API.Toast.success('Range rate plan deleted.', rangeToastOptions);
			} catch (error) {
				API.Toast.error(error.message || 'Unable to delete range rate plan.', rangeToastOptions);
				this.setState({deletingRatePlan: null});
				return;
			}
		}

		this.configurationRevision += 1;
		this.setState((previousState) => {
			let ratePlans = previousState.ratePlans
				.filter((rangeRatePlan, ratePlanIndex) => ratePlan.pkey ? rangeRatePlan.pkey !== ratePlan.pkey : ratePlanIndex !== index)
				.map((rangeRatePlan, ratePlanIndex) => ({
					...rangeRatePlan,
					sort_order: ratePlanIndex,
				}));

			if (!ratePlans.some(rangeRatePlan => rangeRatePlan.is_default)) {
				ratePlans = ratePlans.map((rangeRatePlan, ratePlanIndex) => ({
					...rangeRatePlan,
					is_default: ratePlanIndex === 0,
				}));
			}

			return {
				ratePlans,
				deletingRatePlan: null,
				configurationDirty: previousState.configurationDirty || !ratePlan.pkey,
			};
		});
	};

	saveConfiguration = async () => {
		const configurationRevision = this.configurationRevision;
		this.setState({saving: true});
		try {
			await this.apiRequest('POST', '/range_manager/configuration', {
				settings: this.state.settings,
				lanes: this.state.lanes,
				rate_plans: this.state.ratePlans,
			});
			API.Toast.success('Range Manager configuration saved.', rangeToastOptions);
			if (this.configurationRevision === configurationRevision) {
				this.setState({configurationDirty: false});
				await this.loadDashboard({replaceConfiguration: true, configurationRevision});
			}
		} catch (error) {
			API.Toast.error(error.message || 'Unable to save Range Manager configuration.', rangeToastOptions);
		}
		this.setState({saving: false});
	};

	updateLaneCountTarget = (laneCountTarget) => {
		this.configurationRevision += 1;
		this.setState({
			laneCountTarget,
			laneCountDirty: true,
		});
	};

	createBooking = async (lane, startNow = null) => {
		const {customer_mode, customers, start_mode, ...bookingForm} = this.state.bookingForm;
		const ratePlan = this.getSelectedBookingRatePlan();
		const additionalUnits = this.getClampedAdditionalUnits(bookingForm.additional_units, ratePlan);
		const bookingPreview = this.getBookingFormPreview();
		const paymentPayload = this.getBookingPaymentPayload(bookingPreview.estimatedTotal);
		const laneName = this.getLaneName(lane);
		const selectedLane = this.state.lanes.find(rangeLane => `${rangeLane.pkey}` === `${lane}`);
		const partySize = Math.max(parseInt(bookingForm.party_size, 10) || 1, 1);
		const bookingStartsNow = typeof startNow === 'boolean' ? startNow : start_mode !== 'scheduled';
		const bookingScheduledStart = bookingStartsNow ? moment()
			.format('YYYY-MM-DDTHH:mm') : bookingForm.scheduled_start;
		const bookingActionLabel = bookingStartsNow ? `start ${laneName} now` : `schedule ${laneName}`;

		if (!lane) {
			API.Toast.error('Select a lane before starting or scheduling the booking.', rangeToastOptions);
			return;
		}

		if (selectedLane && partySize > Math.max(parseInt(selectedLane.capacity, 10) || 1, 1) && !window.confirm(`${laneName} has a capacity of ${selectedLane.capacity || 1}, but the party size is ${partySize}. Continue anyway?`)) {
			return;
		}

		if (!this.validateBookingPayment(bookingPreview.estimatedTotal)) {
			return;
		}

		if (paymentPayload.payment_method !== 'none' && !window.confirm(`${paymentPayload.payment_method === 'cash' ? 'Record' : 'Charge'} ${this.formatCurrency(bookingPreview.estimatedTotal)} by ${paymentPayload.payment_method === 'cash' ? 'cash' : 'terminal'} and ${bookingActionLabel}?`)) {
			return;
		}

		const submitPaymentPayload = paymentPayload.charge_with_ipospays ?
			await this.startTerminalPaymentState(paymentPayload, 'Terminal is ready. Ask the customer to tap, insert, or swipe their card.') :
			paymentPayload;
		try {
			const response = await this.apiRequest('PUT', '/range_manager/booking', {
				...bookingForm,
				reserved_units: additionalUnits + 1,
				lane,
				start_now: bookingStartsNow,
				status: bookingStartsNow ? 'active' : 'scheduled',
				scheduled_start: bookingScheduledStart,
				...submitPaymentPayload,
			});
			if (response && response.transaction) {
				API.Toast.success(paymentPayload.payment_method === 'cash' ? 'Lane cash payment recorded.' : 'Lane charge approved.', rangeToastOptions);
			}
			this.setState({bookingForm: {...this.emptyBookingForm(), ...this.getCurrentPaymentSettings()}});
			await this.loadDashboard();
		} catch (error) {
			if (paymentPayload.charge_with_ipospays && this.paymentAbortRequested) {
				API.Toast.success('Terminal charge was cancelled.', rangeToastOptions);
			} else {
				API.Toast.error(error.message || 'Unable to create booking.', rangeToastOptions);
			}
		} finally {
			if (paymentPayload.charge_with_ipospays) {
				this.clearTerminalPaymentState();
			}
		}
	};

	runBookingAction = async (path, booking, extra = {}) => {
		const processingPayment = !!extra.charge_with_ipospays;
		const actionPayload = processingPayment ?
			await this.startTerminalPaymentState(extra, 'Terminal is ready to charge the added lane time.') :
			extra;

		try {
			const response = await this.apiRequest('POST', `/range_manager/${path}`, {
				pkey: booking.pkey,
				...actionPayload,
			});
			if (response && response.transaction) {
				API.Toast.success(actionPayload.payment_method === 'cash' ? 'Lane cash payment recorded.' : 'Lane charge approved.', rangeToastOptions);
			}
			await this.loadDashboard();
		} catch (error) {
			if (processingPayment && this.paymentAbortRequested) {
				API.Toast.success('Terminal charge was cancelled.', rangeToastOptions);
			} else {
				API.Toast.error(error.message || 'Unable to update booking.', rangeToastOptions);
			}
		} finally {
			if (processingPayment) {
				this.clearTerminalPaymentState();
			}
		}
	};

	addWaitlistEntry = async () => {
		const waitlistForm = {...this.state.waitlistForm};
		delete waitlistForm.customer_mode;
		delete waitlistForm.customers;

		try {
			await this.apiRequest('PUT', '/range_manager/waitlist', waitlistForm);
			this.setState({waitlistForm: this.emptyWaitlistForm()});
			await this.loadDashboard();
		} catch (error) {
			API.Toast.error(error.message || 'Unable to add waitlist entry.', rangeToastOptions);
		}
	};

	promoteWaitlistEntry = async (entry) => {
		const lane = this.state.promoteLaneByEntry[entry.pkey];
		if (!lane) {
			API.Toast.error('Select a lane before promoting the waitlist entry.', rangeToastOptions);
			return;
		}

		const waitlistPreview = this.getWaitlistEntryPreview(entry);
		const paymentPayload = this.getBookingPaymentPayload(waitlistPreview.estimatedTotal);
		if (!this.validateBookingPayment(waitlistPreview.estimatedTotal)) {
			return;
		}

		if (paymentPayload.payment_method !== 'none' && !window.confirm(`${paymentPayload.payment_method === 'cash' ? 'Record' : 'Charge'} ${this.formatCurrency(waitlistPreview.estimatedTotal)} by ${paymentPayload.payment_method === 'cash' ? 'cash' : 'terminal'} and promote ${entry.customer_name || 'this waitlist entry'} to ${this.getLaneName(lane)}?`)) {
			return;
		}

		const submitPaymentPayload = paymentPayload.charge_with_ipospays ?
			await this.startTerminalPaymentState(paymentPayload, 'Terminal is ready. Ask the customer to tap, insert, or swipe their card.') :
			paymentPayload;
		try {
			const response = await this.apiRequest('POST', '/range_manager/promote_waitlist', {
				pkey: entry.pkey,
				lane,
				...submitPaymentPayload,
			});
			if (response && response.transaction) {
				API.Toast.success(paymentPayload.payment_method === 'cash' ? 'Lane cash payment recorded.' : 'Lane charge approved.', rangeToastOptions);
			}
			await this.loadDashboard();
		} catch (error) {
			if (paymentPayload.charge_with_ipospays && this.paymentAbortRequested) {
				API.Toast.success('Terminal charge was cancelled.', rangeToastOptions);
			} else {
				API.Toast.error(error.message || 'Unable to promote waitlist entry.', rangeToastOptions);
			}
		} finally {
			if (paymentPayload.charge_with_ipospays) {
				this.clearTerminalPaymentState();
			}
		}
	};

	removeWaitlistEntry = async (entry) => {
		try {
			await this.apiRequest('POST', '/range_manager/remove_waitlist', {
				pkey: entry.pkey,
			});
			await this.loadDashboard();
		} catch (error) {
			API.Toast.error(error.message || 'Unable to remove waitlist entry.', rangeToastOptions);
		}
	};

	createBlock = async () => {
		const blockForm = {...this.state.blockForm};
		const editingBlock = !!blockForm.pkey;
		if (!moment(blockForm.starts_at).isValid() || !moment(blockForm.ends_at).isAfter(moment(blockForm.starts_at))) {
			API.Toast.error('Block end time must be after its start time.', rangeToastOptions);
			return;
		}

		this.setState({blockSaving: true});
		try {
			await this.apiRequest(editingBlock ? 'POST' : 'PUT', '/range_manager/block', blockForm);
			this.setState({blockForm: this.emptyBlockForm()});
			API.Toast.success(editingBlock ? 'Lane block updated.' : 'Lane time blocked.', rangeToastOptions);
			await this.loadDashboard();
		} catch (error) {
			API.Toast.error(error.message || 'Unable to block lane time.', rangeToastOptions);
		}
		this.setState({blockSaving: false});
	};

	editBlock = (block) => {
		this.setState({
			blockForm: {
				pkey: block.pkey,
				lane: block.lane || '',
				title: block.title || '',
				block_type: block.block_type || 'other',
				starts_at: moment(block.starts_at).format('YYYY-MM-DDTHH:mm'),
				ends_at: moment(block.ends_at).format('YYYY-MM-DDTHH:mm'),
				notes: block.notes || '',
			},
		}, () => {
			if (this.blockPanelRef.current) {
				this.blockPanelRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
			}
		});
	};

	cancelBlockEdit = () => this.setState({blockForm: this.emptyBlockForm()});

	deleteBlock = async (block) => {
		if (!window.confirm(`Remove ${block.title || 'this lane block'}?`)) {
			return;
		}

		try {
			await this.apiRequest('DELETE', '/range_manager/block', {pkey: block.pkey});
			if (`${this.state.blockForm.pkey}` === `${block.pkey}`) {
				this.setState({blockForm: this.emptyBlockForm()});
			}
			API.Toast.success('Lane block removed.', rangeToastOptions);
			await this.loadDashboard();
		} catch (error) {
			API.Toast.error(error.message || 'Unable to remove lane block.', rangeToastOptions);
		}
	};

	startBlockForDay = (day, lane = '') => {
		const startsAt = moment(day).hour(9).minute(0).second(0);
		this.setState({
			calendarDate: startsAt.format('YYYY-MM-DD'),
			blockForm: {
				...this.emptyBlockForm(),
				lane,
				starts_at: startsAt.format('YYYY-MM-DDTHH:mm'),
				ends_at: startsAt.clone().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
			},
		}, () => {
			if (this.blockPanelRef.current) {
				this.blockPanelRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
			}
		});
	};

	setCalendarDate = async (value) => {
		this.setState({
			calendarDate: value,
			calendarExpandedDay: '',
		}, this.syncCalendarDashboardData);
	};

	setCalendarViewMode = (calendarViewMode) => {
		this.setState({
			calendarViewMode,
			calendarExpandedDay: calendarViewMode === 'day' ? '' : this.state.calendarExpandedDay,
		}, this.syncCalendarDashboardData);
	};

	shiftCalendarDate = (direction) => {
		const unitByView = {
			day: 'day',
			week: 'week',
			month: 'month',
		};
		const unit = unitByView[this.state.calendarViewMode] || 'day';
		const calendarDate = moment(this.state.calendarDate)
			.add(direction, unit)
			.format('YYYY-MM-DD');

		this.setState({
			calendarDate,
			calendarExpandedDay: '',
		}, this.syncCalendarDashboardData);
	};

	resetCalendarDate = () => {
		const calendarDate = moment().format('YYYY-MM-DD');
		this.setState({
			calendarDate,
			calendarExpandedDay: '',
		}, this.syncCalendarDashboardData);
	};

	syncCalendarDashboardData = () => {
		const calendarQuery = this.getCalendarQuery();
		const visibleCalendarData = this.getVisibleCalendarData();

		if (this.state.calendarDataKey !== calendarQuery.key || !visibleCalendarData.hasCurrentCalendarData) {
			this.loadDashboard();
		}
	};

	showCalendarDayDetails = (calendarDay) => {
		this.setState({calendarExpandedDay: calendarDay}, () => {
			if (!this.calendarDayDetailsRef.current) {
				return;
			}

			window.requestAnimationFrame(() => {
				if (this.calendarDayDetailsRef.current) {
					this.calendarDayDetailsRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					});
				}
			});
		});
	};

	availableLanes = () => this.state.lanes.filter(lane => lane.status === 'active' && !lane.current_booking);

	getActiveRatePlans = (ratePlans = this.state.ratePlans) => ratePlans
		.filter(ratePlan => ratePlan.active)
		.sort((firstPlan, secondPlan) => Number(!!secondPlan.is_default) - Number(!!firstPlan.is_default));

	getDefaultRatePlanFromList = (ratePlans = this.state.ratePlans) => this.getActiveRatePlans(ratePlans)[0] || ratePlans.find(ratePlan => ratePlan.is_default) || ratePlans[0] || {};

	getDefaultRatePlan = () => this.getDefaultRatePlanFromList(this.state.ratePlans);

	getRatePlanByPkey = (ratePlanPkey) => this.state.ratePlans.find(ratePlan => String(ratePlan.pkey) === String(ratePlanPkey)) || null;

	getSelectedBookingRatePlan = () => {
		const selectedRatePlan = this.getRatePlanByPkey(this.state.bookingForm.rate_plan);
		return selectedRatePlan || this.getDefaultRatePlan();
	};

	getBookingRatePlan = (booking) => this.getRatePlanByPkey(booking.rate_plan) || this.getDefaultRatePlan();

	getRatePlanBaseMinutes = (ratePlan) => {
		if (ratePlan.base_unit_minutes) {
			return Math.max(parseInt(ratePlan.base_unit_minutes, 10) || 30, 1);
		}

		if (typeof ratePlan.additional_unit_minutes === 'undefined' && ratePlan.unit_minutes) {
			return Math.max((parseInt(ratePlan.unit_minutes, 10) || 30) * (parseInt(ratePlan.minimum_units, 10) || 1), 1);
		}

		return Math.max((parseInt(this.state.settings.default_unit_minutes, 10) || 30) * (parseInt(this.state.settings.minimum_units, 10) || 1), 1);
	};

	getRatePlanAdditionalMinutes = (ratePlan) => Math.max(parseInt(ratePlan.additional_unit_minutes || ratePlan.unit_minutes || this.state.settings.default_unit_minutes || 30, 10) || 30, 1);

	getRatePlanBasePrice = (ratePlan) => Number(ratePlan.base_price || 0) || 0;

	getRatePlanAdditionalPrice = (ratePlan, fallbackPrice = 0) => {
		if (ratePlan && (ratePlan.additional_unit_price || ratePlan.additional_unit_price === 0)) {
			return Number(ratePlan.additional_unit_price || 0) || 0;
		}

		return Number(fallbackPrice || 0) || 0;
	};

	getLocationTaxValue = (location, snakeName, pascalName) => Number(location && (location[snakeName] ?? location[pascalName]) || 0) || 0;

	getRootLocationRateRows = () => {
		const location = this.props.rootState && this.props.rootState.location ? this.props.rootState.location : null;
		if (!location) {
			return [];
		}

		return [
			{name: 'State Tax', rate: this.getLocationTaxValue(location, 'tax_rate_state', 'TaxRateState'), taxable: true},
			{name: 'County Tax', rate: this.getLocationTaxValue(location, 'tax_rate_county', 'TaxRateCounty'), taxable: true},
			{name: 'Special Tax', rate: this.getLocationTaxValue(location, 'tax_rate_special', 'TaxRateSpecial'), taxable: true},
			{name: 'City Tax', rate: this.getLocationTaxValue(location, 'tax_rate_city', 'TaxRateCity'), taxable: true},
		];
	};

	getRootLocationTaxComponents = () => {
		const rootLocationRateRows = this.getRootLocationRateRows();
		if (!rootLocationRateRows.length) {
			return [];
		}

		return rootLocationRateRows
			.filter(taxRate => taxRate.taxable && taxRate.rate > 0)
			.map(taxRate => ({name: taxRate.name, rate: taxRate.rate}));
	};

	getLocationTaxComponents = () => {
		const rootLocationTaxComponents = this.getRootLocationTaxComponents();
		if (rootLocationTaxComponents.length) {
			return rootLocationTaxComponents;
		}

		return (Array.isArray(this.state.settings.location_tax_rates) ? this.state.settings.location_tax_rates : [])
			.filter(taxRate => taxRate && (Number(taxRate.rate || 0) || 0) > 0);
	};

	getLocationTaxRate = () => {
		const componentTaxRate = this.getLocationTaxComponents()
			.reduce((totalRate, taxRate) => totalRate + (Number(taxRate.rate || 0) || 0), 0);

		return componentTaxRate || Number(this.state.settings.location_tax_rate || 0) || 0;
	};

	getCreditCardFeeRate = () => {
		const location = this.props.rootState && this.props.rootState.location ? this.props.rootState.location : null;
		const rootLocationRate = this.getLocationTaxValue(location, 'credit_card_fee', 'CreditCardFee');
		return rootLocationRate || Number(this.state.settings.credit_card_fee || 0) || 0;
	};

	shouldApplyCreditCardFee = () => {
		return !!this.state.bookingForm.charge_with_ipospays &&
			this.state.bookingForm.payment_method !== 'cash' &&
			!!this.state.bookingForm.include_credit_card_fee &&
			this.getCreditCardFeeRate() > 0;
	};

	getCreditCardFeeAmount = (amount) => {
		const creditCardFeeRate = this.getCreditCardFeeRate();
		const creditCardFee = (Number(amount || 0) || 0) * (creditCardFeeRate / 100);
		return creditCardFeeRate > 0 ? Math.ceil((creditCardFee * 100).toFixed(3)) / 100 : 0;
	};

	formatTaxRate = (rate = this.getLocationTaxRate()) => `${(Number(rate || 0) || 0).toFixed(3).replace(/\.?0+$/, '')}%`;

	getTaxAmount = (subtotal, taxRate = this.getLocationTaxRate()) => {
		const taxTotal = (Number(subtotal || 0) || 0) * ((Number(taxRate || 0) || 0) / 100);
		return Math.ceil((taxTotal * 100).toFixed(3)) / 100;
	};

	getBookingPriceBreakdown = (subtotal) => {
		const taxRate = this.getLocationTaxRate();
		const taxAmount = this.getTaxAmount(subtotal, taxRate);
		const totalBeforeFee = (Number(subtotal || 0) || 0) + taxAmount;
		const creditCardFeeAmount = this.shouldApplyCreditCardFee() ? this.getCreditCardFeeAmount(totalBeforeFee) : 0;
		return {
			subtotal: Number(subtotal || 0) || 0,
			taxAmount,
			totalBeforeFee,
			creditCardFeeAmount,
			creditCardFeeRate: this.getCreditCardFeeRate(),
			total: totalBeforeFee + creditCardFeeAmount,
			taxRate,
		};
	};

	getRatePlanPriceMultiplier = (ratePlan, partySize) => ratePlan && ratePlan.pricing_type === 'per_shooter' ? Math.max(parseInt(partySize, 10) || 1, 1) : 1;

	getRatePlanMaximumAdditionalUnits = (ratePlan) => {
		const maximumAdditionalUnits = ratePlan.maximum_additional_units;
		if (maximumAdditionalUnits === null || maximumAdditionalUnits === '') {
			return null;
		}

		return Math.max(parseInt(maximumAdditionalUnits, 10) || 0, 0);
	};

	getClampedAdditionalUnits = (additionalUnits, ratePlan) => {
		const requestedAdditionalUnits = Math.max(parseInt(additionalUnits, 10) || 0, 0);
		const maximumAdditionalUnits = this.getRatePlanMaximumAdditionalUnits(ratePlan);

		return maximumAdditionalUnits === null ? requestedAdditionalUnits : Math.min(requestedAdditionalUnits, maximumAdditionalUnits);
	};

	formatCurrency = (amount) => `$${(Number(amount || 0) || 0).toFixed(2)}`;

	formatPhoneNumber = (phone) => {
		const digits = `${phone || ''}`.replace(/\D/g, '').slice(0, 10);
		if (digits.length <= 3) {
			return digits;
		}
		if (digits.length <= 6) {
			return `${digits.slice(0, 3)}-${digits.slice(3)}`;
		}
		return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
	};

	getCurrentPaymentSettings = () => ({
		charge_with_ipospays: this.state.bookingForm.charge_with_ipospays,
		include_credit_card_fee: this.state.bookingForm.include_credit_card_fee,
		payment_method: this.state.bookingForm.payment_method,
		register: this.state.bookingForm.register,
		terminal: this.state.bookingForm.terminal,
	});

	getBookingPaymentPayload = (amount) => {
		const shouldCollectPayment = !!this.state.bookingForm.charge_with_ipospays && (Number(amount || 0) || 0) > 0;
		const paymentMethod = this.state.bookingForm.payment_method === 'cash' ? 'cash' : 'ipospays';

		return {
			payment_method: shouldCollectPayment ? paymentMethod : 'none',
			charge_with_ipospays: shouldCollectPayment && paymentMethod === 'ipospays',
			charge_with_cash: shouldCollectPayment && paymentMethod === 'cash',
			include_credit_card_fee: shouldCollectPayment && paymentMethod === 'ipospays' && this.shouldApplyCreditCardFee(),
			register: this.state.bookingForm.register,
			terminal: this.state.bookingForm.terminal,
		};
	};

	getIpospaysTokenStatusMessage = (response) => {
		const tokenStatus = response && response.ipospays_token_status ? response.ipospays_token_status : null;
		if (!tokenStatus) {
			return '';
		}

		const returned = [];
		if (tokenStatus.customer_id_returned) {
			returned.push('customer id');
		}
		if (tokenStatus.card_token_returned) {
			returned.push('card token');
		}

		return returned.length ? ` ${returned.join(' and ')} returned.` : ' No card token returned.';
	};

	validateBookingPayment = (amount) => {
		const paymentPayload = this.getBookingPaymentPayload(amount);
		if (!paymentPayload.charge_with_ipospays) {
			return true;
		}

		if (paymentPayload.payment_method === 'cash') {
			return true;
		}

		if (!this.props.rootState.altrupay_linked) {
			API.Toast.error('Terminal payments are not connected for this account.', rangeToastOptions);
			return false;
		}

		if (!paymentPayload.terminal) {
			API.Toast.error('Select a terminal before charging.', rangeToastOptions);
			return false;
		}

		return true;
	};

	getBookingSubtotalForMinutes = (ratePlan, minutes, partySize) => {
		const bookingMinutes = Math.max(parseInt(minutes, 10) || 0, 0);
		if (bookingMinutes <= 0) {
			return 0;
		}

		const baseMinutes = this.getRatePlanBaseMinutes(ratePlan);
		const additionalMinutes = this.getRatePlanAdditionalMinutes(ratePlan);
		const basePrice = this.getRatePlanBasePrice(ratePlan);
		const additionalPrice = this.getRatePlanAdditionalPrice(ratePlan);
		const priceMultiplier = this.getRatePlanPriceMultiplier(ratePlan, partySize);
		const subtotal = bookingMinutes <= baseMinutes
			? basePrice * (bookingMinutes / baseMinutes)
			: basePrice + (((bookingMinutes - baseMinutes) / additionalMinutes) * additionalPrice);

		return Math.max(Math.round((subtotal * priceMultiplier) * 100) / 100, 0);
	};

	getBookingFormPreview = () => {
		const ratePlan = this.getSelectedBookingRatePlan();
		const additionalUnits = this.getClampedAdditionalUnits(this.state.bookingForm.additional_units, ratePlan);
		const baseMinutes = this.getRatePlanBaseMinutes(ratePlan);
		const additionalMinutes = this.getRatePlanAdditionalMinutes(ratePlan);
		const addedMinutes = additionalUnits * additionalMinutes;
		const partySize = Math.max(parseInt(this.state.bookingForm.party_size, 10) || 1, 1);
		const priceMultiplier = this.getRatePlanPriceMultiplier(ratePlan, partySize);
		const totalMinutes = baseMinutes + addedMinutes;
		const estimatedSubtotalBeforeRangeMinutes = (this.getRatePlanBasePrice(ratePlan) + (additionalUnits * this.getRatePlanAdditionalPrice(ratePlan))) * priceMultiplier;
		const availableRangeMinutes = parseInt(this.state.bookingRangeMinutes.available_minutes, 10) || 0;
		const rangeMinutesApplied = this.state.bookingForm.use_range_minutes ? Math.min(availableRangeMinutes, totalMinutes) : 0;
		const rangeMinuteSubtotal = this.getBookingSubtotalForMinutes(ratePlan, rangeMinutesApplied, partySize);
		const estimatedSubtotal = Math.max(Math.round((estimatedSubtotalBeforeRangeMinutes - rangeMinuteSubtotal) * 100) / 100, 0);
		const priceBreakdown = this.getBookingPriceBreakdown(estimatedSubtotal, ratePlan);
		const customerName = this.state.bookingForm.customer_name || 'Walk-in Customer';

		return {
			customerName,
			additionalUnits,
			baseMinutes,
			addedMinutes,
			totalMinutes,
			availableRangeMinutes,
			rangeMinutesApplied,
			rangeMinutesRemaining: Math.max(availableRangeMinutes - rangeMinutesApplied, 0),
			estimatedSubtotalBeforeRangeMinutes,
			rangeMinuteSubtotal,
			estimatedSubtotal: priceBreakdown.subtotal,
			estimatedTax: priceBreakdown.taxAmount,
			estimatedBeforeFee: priceBreakdown.totalBeforeFee,
			estimatedCreditCardFee: priceBreakdown.creditCardFeeAmount,
			estimatedTotal: priceBreakdown.total,
			creditCardFeeRate: priceBreakdown.creditCardFeeRate,
			taxRate: priceBreakdown.taxRate,
			priceMode: ratePlan.pricing_type === 'per_shooter' ? `Party ${partySize} / per shooter` : 'Per lane',
		};
	};

	getWaitlistEntryPreview = (entry) => {
		const ratePlan = this.getRatePlanByPkey(entry.requested_rate_plan) || this.getDefaultRatePlan();
		const baseMinutes = this.getRatePlanBaseMinutes(ratePlan);
		const additionalMinutes = this.getRatePlanAdditionalMinutes(ratePlan);
		const requestedMinutes = Math.max(parseInt(entry.requested_minutes || baseMinutes, 10) || baseMinutes, 1);
		const additionalUnits = requestedMinutes <= baseMinutes ? 0 : Math.max(Math.ceil((requestedMinutes - baseMinutes) / Math.max(additionalMinutes, 1)), 0);
		const partySize = Math.max(parseInt(entry.party_size, 10) || 1, 1);
		const priceMultiplier = this.getRatePlanPriceMultiplier(ratePlan, partySize);
		const estimatedSubtotal = (this.getRatePlanBasePrice(ratePlan) + (additionalUnits * this.getRatePlanAdditionalPrice(ratePlan))) * priceMultiplier;
		const priceBreakdown = this.getBookingPriceBreakdown(estimatedSubtotal);

		return {
			additionalUnits,
			baseMinutes,
			requestedMinutes,
			estimatedSubtotal: priceBreakdown.subtotal,
			estimatedTax: priceBreakdown.taxAmount,
			estimatedBeforeFee: priceBreakdown.totalBeforeFee,
			estimatedCreditCardFee: priceBreakdown.creditCardFeeAmount,
			estimatedTotal: priceBreakdown.total,
			creditCardFeeRate: priceBreakdown.creditCardFeeRate,
			partySize,
			priceMode: ratePlan.pricing_type === 'per_shooter' ? `Party ${partySize} / per shooter` : 'Per lane',
			rateName: ratePlan.name || 'Default',
		};
	};

	getAddTimeNotice = (booking) => {
		const ratePlan = this.getBookingRatePlan(booking);
		const partySize = Math.max(parseInt(booking.party_size, 10) || 1, 1);
		const additionalMinutes = this.getRatePlanAdditionalMinutes(ratePlan);
		const priceMultiplier = this.getRatePlanPriceMultiplier(ratePlan, partySize);
		const estimatedSubtotal = this.getRatePlanAdditionalPrice(ratePlan, booking.price_per_unit) * priceMultiplier;
		const priceBreakdown = this.getBookingPriceBreakdown(estimatedSubtotal, ratePlan);
		const maximumAdditionalUnits = this.getRatePlanMaximumAdditionalUnits(ratePlan);
		const reservedUnits = Math.max(parseInt(booking.reserved_units, 10) || 1, 1);
		const extendedUnits = Math.max(parseInt(booking.extended_units, 10) || 0, 0);
		const addedUnitsUsed = (reservedUnits - 1) + extendedUnits;
		const extensionsDisabled = !this.state.settings.allow_lane_extensions;
		const maximumReached = maximumAdditionalUnits !== null && addedUnitsUsed >= maximumAdditionalUnits;
		const disabledReason = extensionsDisabled ? 'Lane extensions disabled' : (maximumReached ? 'Max added time reached' : '');

		return {
			additionalMinutes,
			estimatedCharge: priceBreakdown.total,
			estimatedSubtotal: priceBreakdown.subtotal,
			estimatedTax: priceBreakdown.taxAmount,
			estimatedBeforeFee: priceBreakdown.totalBeforeFee,
			estimatedCreditCardFee: priceBreakdown.creditCardFeeAmount,
			creditCardFeeRate: priceBreakdown.creditCardFeeRate,
			partySize,
			priceMode: ratePlan.pricing_type === 'per_shooter' ? `for party of ${partySize}` : 'per lane',
			label: `Adds ${additionalMinutes} min / +${this.formatCurrency(priceBreakdown.total)} est.`,
			disabled: extensionsDisabled || maximumReached,
			disabledReason,
		};
	};

	confirmAddTime = (booking) => {
		const addTimeNotice = this.getAddTimeNotice(booking);
		const paymentPayload = this.getBookingPaymentPayload(addTimeNotice.estimatedCharge);

		if (addTimeNotice.disabled) {
			API.Toast.error(addTimeNotice.disabledReason || 'Unable to add time to this lane.', rangeToastOptions);
			return;
		}

		if (!this.validateBookingPayment(addTimeNotice.estimatedCharge)) {
			return;
		}

		const customerName = booking.customer_name || 'Walk-in Customer';
		const laneName = this.getLaneName(booking.lane);
		const paymentMessage = paymentPayload.payment_method === 'cash' ?
			'\n\nThis will record cash payment before the lane is extended.' :
			(paymentPayload.charge_with_ipospays ? '\n\nThis will charge the selected terminal before the lane is extended.' : '');
		const confirmed = window.confirm(
			`Add ${addTimeNotice.additionalMinutes} minutes to ${customerName} on ${laneName}?\n\nEstimated added charge: ${this.formatCurrency(addTimeNotice.estimatedCharge)} ${addTimeNotice.priceMode}.${paymentMessage}\nConfirm the customer accepts this charge before continuing.`
		);

		if (!confirmed) {
			return;
		}

		this.runBookingAction('extend', booking, {
			units_delta: 1,
			...paymentPayload,
		});
	};

	refundBooking = async (booking) => {
		const refundableAmount = Number(booking.refundable_amount || 0) || 0;
		if (refundableAmount <= 0) {
			API.Toast.error('No refundable Range Manager payment was found for this lane.', rangeToastOptions);
			return;
		}

		if (!window.confirm(`Refund ${this.formatCurrency(refundableAmount)} for ${booking.customer_name || 'this customer'}?`)) {
			return;
		}

		try {
			await this.apiRequest('POST', '/range_manager/refund', {
				pkey: booking.pkey,
			});
			API.Toast.success('Range payment refunded.', rangeToastOptions);
			await this.loadDashboard();
		} catch (error) {
			API.Toast.error(error.message || 'Unable to refund that range payment.', rangeToastOptions);
		}
	};

	getCalendarRange = (calendarDate = this.state.calendarDate, calendarViewMode = this.state.calendarViewMode) => {
		const selectedDate = moment(calendarDate || undefined);

		if (calendarViewMode === 'week') {
			return {
				start: selectedDate.clone().startOf('week'),
				end: selectedDate.clone().endOf('week'),
				label: `${selectedDate.clone().startOf('week').format('MMM D')} - ${selectedDate.clone().endOf('week').format('MMM D, YYYY')}`,
			};
		}

		if (calendarViewMode === 'month') {
			return {
				start: selectedDate.clone().startOf('month').startOf('week'),
				end: selectedDate.clone().endOf('month').endOf('week'),
				label: selectedDate.format('MMMM YYYY'),
			};
		}

		return {
			start: selectedDate.clone().startOf('day'),
			end: selectedDate.clone().endOf('day'),
			label: selectedDate.format('dddd, MMMM D, YYYY'),
		};
	};

	getCalendarLoadRange = (calendarDate = this.state.calendarDate) => {
		const selectedDate = moment(calendarDate || undefined);

		return {
			start: selectedDate.clone().subtract(1, 'month').startOf('month').startOf('week'),
			end: selectedDate.clone().add(1, 'month').endOf('month').endOf('week'),
		};
	};

	getCalendarQuery = (calendarDate = this.state.calendarDate) => {
		const calendarRange = this.getCalendarLoadRange(calendarDate);
		const date = calendarDate || moment().format('YYYY-MM-DD');
		const dateStart = calendarRange.start.format('YYYY-MM-DD');
		const dateEnd = calendarRange.end.format('YYYY-MM-DD');

		return {
			date,
			dateStart,
			dateEnd,
			viewMode: this.state.calendarViewMode,
			key: `${dateStart}:${dateEnd}`,
		};
	};

	isCalendarRangeLoaded = (rangeStart, rangeEnd) => (
		!!this.state.calendarDataStart &&
		!!this.state.calendarDataEnd &&
		moment(this.state.calendarDataStart).isSameOrBefore(rangeStart, 'day') &&
		moment(this.state.calendarDataEnd).isSameOrAfter(rangeEnd, 'day')
	);

	getVisibleCalendarData = () => {
		const calendarRange = this.getCalendarRange();
		const hasCurrentCalendarData = this.isCalendarRangeLoaded(calendarRange.start, calendarRange.end);

		return {
			bookings: this.state.bookings
				.filter(booking => this.eventOverlapsRange(booking.scheduled_start, booking.scheduled_end, calendarRange.start, calendarRange.end)),
			blocks: this.state.blocks
				.filter(block => this.eventOverlapsRange(block.starts_at, block.ends_at, calendarRange.start, calendarRange.end)),
			hasCurrentCalendarData,
		};
	};

	getLaneName = (lanePkey) => {
		const lane = this.state.lanes.find(rangeLane => String(rangeLane.pkey) === String(lanePkey));
		return lane ? lane.name : 'All lanes';
	};

	bookingToCalendarEvent = (booking) => ({
		key: `booking-${booking.pkey}`,
		type: 'booking',
		title: booking.customer_name || 'Walk-in Customer',
		start: booking.scheduled_start,
		end: booking.scheduled_end,
		status: booking.status,
		lane: booking.lane,
		source: booking,
	});

	blockToCalendarEvent = (block) => ({
		key: `block-${block.pkey}`,
		type: 'block',
		title: block.title || 'Blocked',
		start: block.starts_at,
		end: block.ends_at,
		status: block.block_type,
		lane: block.lane,
		source: block,
	});

	eventOverlapsRange = (eventStart, eventEnd, rangeStart, rangeEnd) => {
		if (!eventStart || !eventEnd) return false;
		return moment(eventStart).isBefore(rangeEnd) && moment(eventEnd).isAfter(rangeStart);
	};

	getCalendarEventsForDay = (day, calendarData = this.getVisibleCalendarData()) => {
		const rangeStart = moment(day).startOf('day');
		const rangeEnd = moment(day).endOf('day');
		const bookingEvents = calendarData.bookings
			.filter(booking => this.eventOverlapsRange(booking.scheduled_start, booking.scheduled_end, rangeStart, rangeEnd))
			.map(booking => this.bookingToCalendarEvent(booking));
		const blockEvents = calendarData.blocks
			.filter(block => this.eventOverlapsRange(block.starts_at, block.ends_at, rangeStart, rangeEnd))
			.map(block => this.blockToCalendarEvent(block));

		return [...bookingEvents, ...blockEvents].sort((left, right) => moment(left.start).valueOf() - moment(right.start).valueOf());
	};

	formatTimeRange = (start, end) => {
		if (!start || !end) return 'Time not set';
		return `${moment(start).format('h:mm A')} - ${moment(end).format('h:mm A')}`;
	};

	getBookingProgress = (booking) => {
		if (!booking || !booking.scheduled_start || !booking.scheduled_end) return 0;

		const start = moment(booking.scheduled_start);
		const end = moment(booking.scheduled_end);
		if (!start.isValid() || !end.isValid()) return 0;

		const totalMinutes = end.diff(start, 'minutes');
		if (totalMinutes <= 0) return 0;

		const elapsedMinutes = this.getCurrentMoment().diff(start, 'minutes');
		return Math.max(0, Math.min(100, Math.round((elapsedMinutes / totalMinutes) * 100)));
	};

	getBookingRemainingLabel = (booking) => {
		if (!booking || !booking.scheduled_end) return 'Time not set';

		const end = moment(booking.scheduled_end);
		if (!end.isValid()) return 'Time not set';

		const remainingMinutes = end.diff(this.getCurrentMoment(), 'minutes');
		if (remainingMinutes < 0) {
			return `${Math.abs(remainingMinutes)} min over`;
		}

		if (remainingMinutes < 60) {
			return `${remainingMinutes} min left`;
		}

		const hours = Math.floor(remainingMinutes / 60);
		const minutes = remainingMinutes % 60;
		return minutes ? `${hours}h ${minutes}m left` : `${hours}h left`;
	};

	isBookingFinishedByTime = (booking) => {
		if (!booking || booking.status !== 'active' || !booking.scheduled_end) return false;

		const end = moment(booking.scheduled_end);
		return end.isValid() && end.isBefore(this.getCurrentMoment());
	};

	getBookingDisplayStatus = (booking) => this.isBookingFinishedByTime(booking) ? 'finished' : (booking.status || '');

	getWaitlistRequestedMinutes = (waitlistEntry) => Math.max(parseInt(waitlistEntry.requested_minutes || this.state.settings.default_unit_minutes || 30, 10) || 30, 1);

	getWaitlistLaneType = (waitlistEntry) => String(waitlistEntry.requested_lane_type || '')
		.trim()
		.toLowerCase();

	laneMatchesWaitlistRequest = (lane, requestedLaneType) => !requestedLaneType || String(lane.lane_type || '')
		.trim()
		.toLowerCase() === requestedLaneType;

	getProjectedLaneAvailability = () => {
		const now = this.getCurrentMoment();

		return this.state.lanes
			.filter(lane => lane.status === 'active')
			.map((lane) => {
				const bookingEnd = lane.current_booking && lane.current_booking.scheduled_end ? moment(lane.current_booking.scheduled_end) : null;

				return {
					lane,
					availableAt: bookingEnd && bookingEnd.isValid() && bookingEnd.isAfter(now) ? bookingEnd : now.clone(),
				};
			});
	};

	getNextProjectedLane = (laneAvailability, requestedLaneType) => laneAvailability
		.filter(laneProjection => this.laneMatchesWaitlistRequest(laneProjection.lane, requestedLaneType))
		.sort((left, right) => left.availableAt.valueOf() - right.availableAt.valueOf())[0] || null;

	formatWaitlistMinutes = (minutes) => {
		if (minutes === null) {
			return 'No matching lane';
		}

		if (minutes <= 0) {
			return 'Ready now';
		}

		if (minutes < 60) {
			return `${minutes} min`;
		}

		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;
		return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
	};

	getWaitlistLaneEstimates = () => {
		const now = this.getCurrentMoment();
		const laneAvailability = this.getProjectedLaneAvailability();
		const entriesByPkey = {};

		this.state.waitlist.forEach((entry) => {
			const requestedLaneType = this.getWaitlistLaneType(entry);
			const nextLaneProjection = this.getNextProjectedLane(laneAvailability, requestedLaneType);

			if (!nextLaneProjection) {
				entriesByPkey[entry.pkey] = {
					minutes: null,
					label: this.formatWaitlistMinutes(null),
				};
				return;
			}

			const waitMinutes = Math.max(nextLaneProjection.availableAt.diff(now, 'minutes'), 0);
			entriesByPkey[entry.pkey] = {
				minutes: waitMinutes,
				label: this.formatWaitlistMinutes(waitMinutes),
				lane: nextLaneProjection.lane,
				startsAt: nextLaneProjection.availableAt.clone(),
			};
			nextLaneProjection.availableAt = nextLaneProjection.availableAt.clone()
				.add(this.getWaitlistRequestedMinutes(entry), 'minutes');
		});

		const requestedLaneType = this.getWaitlistLaneType(this.state.waitlistForm);
		const nextLaneProjection = this.getNextProjectedLane(laneAvailability, requestedLaneType);
		const nextEntryWaitMinutes = nextLaneProjection ? Math.max(nextLaneProjection.availableAt.diff(now, 'minutes'), 0) : null;

		return {
			entriesByPkey,
			nextEntry: {
				minutes: nextEntryWaitMinutes,
				label: this.formatWaitlistMinutes(nextEntryWaitMinutes),
				lane: nextLaneProjection ? nextLaneProjection.lane : null,
				startsAt: nextLaneProjection ? nextLaneProjection.availableAt.clone() : null,
			},
		};
	};

	renderSummary() {
		const {summary} = this.state;
		const activeLaneCount = summary.active_lane_count || 0;
		const occupiedLaneCount = summary.occupied_lane_count || 0;
		const availableLaneCount = Math.max(activeLaneCount - occupiedLaneCount, 0);
		const waitlistCount = summary.waitlist_count || 0;
		const configuredLaneCount = this.state.lanes.length;
		const waitlistEstimate = this.getWaitlistLaneEstimates().nextEntry;
		const summaryGauges = [
			{
				id: 'active',
				label: 'Active Lanes',
				value: activeLaneCount,
				percentage: configuredLaneCount ? (activeLaneCount / configuredLaneCount) * 100 : 0,
				note: `${configuredLaneCount} configured`,
			},
			{
				id: 'occupied',
				label: 'Occupied',
				value: occupiedLaneCount,
				percentage: activeLaneCount ? (occupiedLaneCount / activeLaneCount) * 100 : 0,
				note: `${availableLaneCount} available`,
			},
			{
				id: 'available',
				label: 'Available',
				value: availableLaneCount,
				percentage: activeLaneCount ? (availableLaneCount / activeLaneCount) * 100 : 0,
				note: `${occupiedLaneCount} occupied`,
			},
			{
				id: 'waitlist',
				label: 'Waitlist',
				value: waitlistCount,
				percentage: activeLaneCount ? (waitlistCount / activeLaneCount) * 100 : 0,
				note: `Overall wait: ${waitlistEstimate.label}`,
			},
		];

		return (
			<div className="summary-grid">
				{summaryGauges.map(gauge => {
					const gaugePercentage = Math.max(0, Math.min(gauge.percentage, 100));
					return (
						<div key={gauge.id} className={`summary-gauge summary-gauge-${gauge.id}`}>
							<div className="summary-gauge-label">{gauge.label}</div>
							<div className="summary-gauge-arc">
								<svg viewBox="0 0 160 92" role="img" aria-label={`${gauge.label}: ${gauge.value}`}>
									<path className="summary-gauge-track" pathLength="100" d="M 12 78 A 68 68 0 0 1 148 78"/>
									<path
										className="summary-gauge-progress"
										pathLength="100"
										strokeDasharray={`${gaugePercentage} 100`}
										d="M 12 78 A 68 68 0 0 1 148 78"
									/>
								</svg>
								<div className="summary-gauge-reading">
									<strong>{gauge.value}</strong>
									<span>{gauge.label}</span>
								</div>
							</div>
							<div className="summary-gauge-note">{gauge.note}</div>
						</div>
					);
				})}
			</div>
		);
	}

	renderTabs() {
		const tabs = [
			{id: 'live', label: 'Live Lanes', icon: 'fa-th-large'},
			{id: 'calendar', label: 'Calendar', icon: 'fa-calendar'},
			{id: 'configuration', label: 'Configuration', icon: 'fa-sliders'},
		];

		return (
			<div className="range-tabs">
				{tabs.map(tab => (
					<button
						key={tab.id}
						type="button"
						className={`range-tab ${this.state.activeView === tab.id ? 'active' : ''}`}
						onClick={() => this.setActiveView(tab.id)}
					>
						<i className={`fa ${tab.icon}`}/>&nbsp;&nbsp;{tab.label}
					</button>
				))}
			</div>
		);
	}

	renderCreditCardFeeToggle(amountBeforeFee = null) {
		const creditCardFeeRate = this.getCreditCardFeeRate();
		const paymentMethod = this.state.bookingForm.payment_method === 'cash' ? 'cash' : 'ipospays';
		if (paymentMethod !== 'ipospays' || creditCardFeeRate <= 0) {
			return null;
		}

		const feeAmount = amountBeforeFee !== null ? this.getCreditCardFeeAmount(amountBeforeFee) : 0;
		const feeLabel = feeAmount > 0 ?
			`Add credit card fee (${this.formatTaxRate(creditCardFeeRate)} / ${this.formatCurrency(feeAmount)})` :
			`Add credit card fee (${this.formatTaxRate(creditCardFeeRate)})`;

		return (
			<label className="booking-payment-toggle booking-card-fee-toggle">
				<input
					type="checkbox"
					checked={!!this.state.bookingForm.include_credit_card_fee}
					disabled={this.state.paymentProcessing}
					onChange={event => this.updateForm('bookingForm', 'include_credit_card_fee', event.target.checked)}
				/>
				<span>{feeLabel}</span>
			</label>
		);
	}

	renderBookingPaymentControls(bookingPreview) {
		const canCollectPayment = bookingPreview.estimatedTotal > 0;
		const terminalConnected = !!this.props.rootState.altrupay_linked;
		const paymentEnabled = canCollectPayment && !!this.state.bookingForm.charge_with_ipospays;
		const paymentMethod = this.state.bookingForm.payment_method === 'cash' ? 'cash' : 'ipospays';

		return (
			<div className="booking-payment-panel">
				<label className="booking-payment-toggle">
					<input
						type="checkbox"
						checked={paymentEnabled}
						disabled={!canCollectPayment || this.state.paymentProcessing}
						onChange={event => this.updateForm('bookingForm', 'charge_with_ipospays', event.target.checked)}
					/>
					<span>Collect {this.formatCurrency(bookingPreview.estimatedTotal)} at booking</span>
				</label>
				{bookingPreview.estimatedTotal <= 0 ? (
					<div className="muted">No payment is needed for a $0.00 lane booking.</div>
				) : null}
				{paymentEnabled ? (
					<>
						<div className="payment-method-toggle">
							<button
								type="button"
								className={paymentMethod === 'ipospays' ? 'active' : ''}
								disabled={!terminalConnected}
								onClick={() => this.updateForm('bookingForm', 'payment_method', 'ipospays')}
							>
								Terminal
							</button>
							<button
								type="button"
								className={paymentMethod === 'cash' ? 'active' : ''}
								onClick={() => this.updateForm('bookingForm', 'payment_method', 'cash')}
							>
								Cash
							</button>
						</div>
						{!terminalConnected && paymentMethod === 'ipospays' ? (
							<div className="muted">Connect a terminal before charging lane bookings here, or choose Cash.</div>
						) : null}
						{this.renderCreditCardFeeToggle(bookingPreview.estimatedBeforeFee)}
						<div className="two-col booking-payment-fields">
							{paymentMethod === 'cash' ? (
								<Framework.Elements.Select
								shouldLoad={true}
								value={this.state.bookingForm.register}
								error={''}
								model={Types.Registers._name}
								property={registerSelectProperty}
								label={'Register'}
								update={this.updateBookingPaymentSelect}
								nullable={true}
								defaultFirst={true}
								default={{value: '', label: 'Select Register'}}
								/>
							) : null}
							{paymentMethod === 'ipospays' ? (
								<Framework.Elements.Select
									shouldLoad={true}
									value={this.state.bookingForm.terminal}
									error={''}
									model={Types.Terminals._name}
									property={terminalSelectProperty}
									label={'Terminal'}
									update={this.updateBookingPaymentSelect}
									id={'id'}
									text={'description'}
									textHelper={'manufacturer&model'}
									nullable={true}
									defaultFirst={true}
									default={{value: '', label: 'Select Terminal'}}
								/>
							) : null}
						</div>
					</>
				) : null}
			</div>
		);
	}

	renderWaitlistPaymentControls() {
		const terminalConnected = !!this.props.rootState.altrupay_linked;
		const paymentEnabled = !!this.state.bookingForm.charge_with_ipospays;
		const paymentMethod = this.state.bookingForm.payment_method === 'cash' ? 'cash' : 'ipospays';

		return (
			<div className="booking-payment-panel waitlist-payment-panel">
				<label className="booking-payment-toggle">
					<input
						type="checkbox"
						checked={paymentEnabled}
						disabled={this.state.paymentProcessing}
						onChange={event => this.updateForm('bookingForm', 'charge_with_ipospays', event.target.checked)}
					/>
					<span>Collect payment when promoting from waitlist</span>
				</label>
				{paymentEnabled ? (
					<>
						<div className="payment-method-toggle">
							<button
								type="button"
								className={paymentMethod === 'ipospays' ? 'active' : ''}
								disabled={!terminalConnected}
								onClick={() => this.updateForm('bookingForm', 'payment_method', 'ipospays')}
							>
								Terminal
							</button>
							<button
								type="button"
								className={paymentMethod === 'cash' ? 'active' : ''}
								onClick={() => this.updateForm('bookingForm', 'payment_method', 'cash')}
							>
								Cash
							</button>
						</div>
						{!terminalConnected && paymentMethod === 'ipospays' ? (
							<div className="muted">Connect a terminal before charging waitlist promotions here, or choose Cash.</div>
						) : null}
						{this.renderCreditCardFeeToggle()}
						<div className="two-col booking-payment-fields">
							{paymentMethod === 'cash' ? (
								<Framework.Elements.Select
									shouldLoad={true}
									value={this.state.bookingForm.register}
									error={''}
									model={Types.Registers._name}
									property={registerSelectProperty}
									label={'Register'}
									update={this.updateBookingPaymentSelect}
									nullable={true}
									defaultFirst={true}
									default={{value: '', label: 'Select Register'}}
								/>
							) : null}
							{paymentMethod === 'ipospays' ? (
								<Framework.Elements.Select
									shouldLoad={true}
									value={this.state.bookingForm.terminal}
									error={''}
									model={Types.Terminals._name}
									property={terminalSelectProperty}
									label={'Terminal'}
									update={this.updateBookingPaymentSelect}
									id={'id'}
									text={'description'}
									textHelper={'manufacturer&model'}
									nullable={true}
									defaultFirst={true}
									default={{value: '', label: 'Select Terminal'}}
								/>
							) : null}
						</div>
					</>
				) : null}
			</div>
		);
	}

	renderBookingForm(layout = 'stacked') {
		const ratePlan = this.getSelectedBookingRatePlan();
		const baseMinutes = this.getRatePlanBaseMinutes(ratePlan);
		const additionalMinutes = this.getRatePlanAdditionalMinutes(ratePlan);
		const maximumAdditionalUnits = this.getRatePlanMaximumAdditionalUnits(ratePlan);
		const customerMode = this.state.bookingForm.customer_mode || 'existing';
		const bookingStartMode = this.state.bookingForm.start_mode || 'now';
		const bookingPreview = this.getBookingFormPreview();
		const bookingPanelOpen = this.state.bookingPanelOpen;
		const liveOperationsLayout = layout === 'operations';
		const scheduledStart = moment(this.state.bookingForm.scheduled_start);
		const scheduledStartLabel = scheduledStart.isValid() ? scheduledStart.format('MMM D, h:mm A') : 'Not set';
		const startNowLabel = this.getCurrentMoment().format('MMM D, h:mm A');

		return (
			<div className={`panel collapsible-panel booking-panel ${liveOperationsLayout ? 'booking-panel-operations' : ''} ${bookingPanelOpen ? 'open' : 'collapsed'}`}>
				<button
					type="button"
					className="collapsible-panel-header"
					onClick={() => this.togglePanel('bookingPanelOpen')}
					aria-expanded={bookingPanelOpen}
				>
					<span>
						<span className="collapsible-panel-title">Lane Assignment</span>
						<span className="collapsible-panel-subtitle">
							{bookingPreview.totalMinutes} min / {this.formatCurrency(bookingPreview.estimatedTotal)}
						</span>
					</span>
					<i className={`fa ${bookingPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}/>
				</button>
				{bookingPanelOpen ? (
					<div className={`compact-form booking-form-grid ${liveOperationsLayout ? 'booking-form-grid-operations' : ''}`}>
						<div className="booking-op-group booking-op-customer">
							<div className="booking-customer-field">
								<div className="customer-selector-header">
									<label>Customer</label>
									<div className="customer-mode-toggle">
										<button
											type="button"
											className={customerMode === 'existing' ? 'active' : ''}
											onClick={() => this.setBookingCustomerMode('existing')}
										>
											Existing
										</button>
										<button
											type="button"
											className={customerMode === 'new' ? 'active' : ''}
											onClick={() => this.setBookingCustomerMode('new')}
										>
											New
										</button>
									</div>
								</div>
								{customerMode === 'existing' ? (
									<Framework.Elements.Select
										shouldLoad={true}
										update={this.updateBookingCustomer}
										property={customerSelectProperty}
										value={this.state.bookingForm.customer}
										error={''}
										object={this.state.bookingForm.customers}
										text={'name'}
										textHelper={'phone/email'}
										sort={'name'}
										nullable={true}
										default={{value: '', label: 'Select Customer'}}
										showToolTip={true}
									/>
								) : (
									<input
										value={this.state.bookingForm.customer_name}
										onChange={event => this.updateNewBookingCustomer('customer_name', event.target.value)}
										placeholder="Walk-in Customer"
									/>
								)}
							</div>
							<div className="two-col booking-contact-grid">
								<div>
									<label>Phone</label>
										<input
											inputMode="numeric"
											maxLength="12"
											value={this.state.bookingForm.customer_phone}
										onChange={event => this.updateNewBookingCustomer('customer_phone', event.target.value)}
									/>
								</div>
								<div>
									<label>Party Size</label>
									<input
										type="number"
										min="1"
										value={this.state.bookingForm.party_size}
										onChange={event => this.updateForm('bookingForm', 'party_size', event.target.value)}
									/>
								</div>
							</div>
						</div>
						<div className="booking-op-group booking-op-time">
							<div className="two-col booking-rate-grid">
								<div>
									<label>Rate</label>
									<select
										value={this.state.bookingForm.rate_plan}
										onChange={event => this.updateForm('bookingForm', 'rate_plan', event.target.value)}
									>
										{this.getActiveRatePlans().map(rangeRatePlan => (
											<option key={rangeRatePlan.pkey || rangeRatePlan.name} value={rangeRatePlan.pkey || ''}>
												{rangeRatePlan.name}
											</option>
										))}
									</select>
								</div>
								<div>
									<label>Add Units ({additionalMinutes} min)</label>
									<input
										type="number"
										min="0"
										max={maximumAdditionalUnits === null ? undefined : maximumAdditionalUnits}
										value={this.state.bookingForm.additional_units}
										onChange={event => this.updateForm('bookingForm', 'additional_units', event.target.value)}
									/>
								</div>
							</div>
							<div className="muted">
								Base time is {baseMinutes} minutes{maximumAdditionalUnits === null ? '' : `; max ${maximumAdditionalUnits} additional units`}.
							</div>
							{this.state.bookingRangeMinutes.loading ? (
								<div className="muted">Checking membership range minutes...</div>
							) : null}
							{!this.state.bookingRangeMinutes.loading && bookingPreview.availableRangeMinutes > 0 ? (
								<label className="booking-payment-toggle booking-range-minutes-toggle">
									<input
										type="checkbox"
										checked={!!this.state.bookingForm.use_range_minutes}
										disabled={this.state.paymentProcessing}
										onChange={event => this.updateForm('bookingForm', 'use_range_minutes', event.target.checked)}
									/>
									<span>
										Use current range minutes ({bookingPreview.availableRangeMinutes} left)
										{bookingPreview.rangeMinutesApplied > 0 ? ` - applies ${bookingPreview.rangeMinutesApplied} min` : ''}
									</span>
								</label>
							) : null}
							{!this.state.bookingRangeMinutes.loading && this.state.bookingRangeMinutes.error ? (
								<div className="muted">{this.state.bookingRangeMinutes.error}</div>
							) : null}
						</div>
						<div className="booking-op-group booking-op-total">
							<div className="booking-summary">
								<div>
									<span>Customer</span>
									<strong>{bookingPreview.customerName}</strong>
								</div>
								<div>
									<span>Time</span>
									<strong>{bookingPreview.totalMinutes} min</strong>
								</div>
								<div>
									<span>Estimated Total</span>
									<strong>{this.formatCurrency(bookingPreview.estimatedTotal)}</strong>
								</div>
								<div className="booking-summary-note">
									{bookingPreview.addedMinutes ? `${bookingPreview.baseMinutes} min base + ${bookingPreview.addedMinutes} min added` : `${bookingPreview.baseMinutes} min base`} / {bookingPreview.priceMode}
									{bookingPreview.rangeMinutesApplied > 0 ? ` / ${bookingPreview.rangeMinutesApplied} membership min applied` : ''}
									{bookingPreview.estimatedTax > 0 ? ` / ${this.formatCurrency(bookingPreview.estimatedSubtotal)} subtotal + ${this.formatCurrency(bookingPreview.estimatedTax)} tax (${bookingPreview.taxRate.toFixed(3)}%)` : ''}
									{bookingPreview.estimatedCreditCardFee > 0 ? ` + ${this.formatCurrency(bookingPreview.estimatedCreditCardFee)} credit card fee` : ''}
								</div>
							</div>
							{this.renderBookingPaymentControls(bookingPreview)}
						</div>
							<div className="booking-op-group booking-op-schedule">
								<div>
									<label>Lane</label>
									<select
										value={this.state.bookingForm.lane}
										onChange={event => this.updateForm('bookingForm', 'lane', event.target.value)}
									>
										<option value="">Select lane</option>
										{this.state.lanes.filter(lane => lane.status === 'active').map(lane => (
											<option key={lane.pkey || lane.lane_number} value={lane.pkey || ''}>{lane.name} (capacity {lane.capacity || 1})</option>
										))}
									</select>
								</div>
								<div className="booking-start-time-field">
									<label>Start Time</label>
									<div className="customer-mode-toggle">
										<button
											type="button"
											className={bookingStartMode !== 'scheduled' ? 'active' : ''}
											onClick={() => this.setBookingStartMode('now')}
										>
											Start Now
										</button>
										<button
											type="button"
											className={bookingStartMode === 'scheduled' ? 'active' : ''}
											onClick={() => this.setBookingStartMode('scheduled')}
										>
											Schedule Start
										</button>
									</div>
								</div>
							{bookingStartMode === 'scheduled' ? (
								<div>
									<label>Scheduled Start</label>
									<input
										type="datetime-local"
										value={this.state.bookingForm.scheduled_start}
										onChange={event => this.updateForm('bookingForm', 'scheduled_start', event.target.value)}
									/>
								</div>
							) : null}
							<div>
								<label>Notes</label>
								<textarea
									value={this.state.bookingForm.notes}
									onChange={event => this.updateForm('bookingForm', 'notes', event.target.value)}
								/>
							</div>
								<div className="muted">
									{bookingStartMode === 'scheduled' ? `Schedule Start time: ${scheduledStartLabel}` : `Start Now time: ${startNowLabel}`}
								</div>
								{(() => {
									const selectedLane = this.state.lanes.find(lane => `${lane.pkey}` === `${this.state.bookingForm.lane}`);
									const partySize = Math.max(parseInt(this.state.bookingForm.party_size, 10) || 1, 1);
									return selectedLane && partySize > Math.max(parseInt(selectedLane.capacity, 10) || 1, 1) ? (
										<div className="occupancy-warning" role="alert">
											<i className="fa fa-exclamation-triangle"/>&nbsp;Party size {partySize} exceeds {selectedLane.name}'s capacity of {selectedLane.capacity || 1}.
										</div>
									) : null;
								})()}
								<div className="booking-submit-row">
									<Button className="primary-button"
										disabled={!this.state.bookingForm.lane || this.state.paymentProcessing}
										onClick={() => this.createBooking(this.state.bookingForm.lane)}
									>
										<i className={`fa ${bookingStartMode === 'scheduled' ? 'fa-calendar-plus-o' : 'fa-play'}`}/>&nbsp;
										{this.state.paymentProcessing ? 'Charging' : (bookingStartMode === 'scheduled' ? 'Schedule' : 'Start')}
									</Button>
								</div>
						</div>
					</div>
				) : null}
			</div>
		);
	}

	renderLaneBoardLane(lane) {
		const booking = lane.current_booking;
		const bookingFinished = this.isBookingFinishedByTime(booking);
		const stateClass = booking ? (bookingFinished ? 'finished' : 'occupied') : (lane.status === 'active' ? 'open' : (lane.status || 'inactive'));
		const pillClass = booking ? (bookingFinished ? 'green' : 'red') : (lane.status === 'active' ? 'green' : 'amber');
		const statusLabel = booking ? (bookingFinished ? 'Finished' : 'Live') : (lane.status === 'active' ? 'Ready' : (lane.status || 'Inactive'));
		const progress = booking ? this.getBookingProgress(booking) : 0;
		const remainingLabel = booking ? this.getBookingRemainingLabel(booking) : null;
		const defaultPlan = this.getDefaultRatePlan();
		const baseMinutes = this.getRatePlanBaseMinutes(defaultPlan);
		const additionalMinutes = this.getRatePlanAdditionalMinutes(defaultPlan);
		const addTimeNotice = booking ? this.getAddTimeNotice(booking) : null;
		const bookingStartMode = this.state.bookingForm.start_mode || 'now';
		const bookingStartsNow = bookingStartMode !== 'scheduled';
		const bookingStartActionLabel = bookingStartsNow ? 'Start Now' : 'Schedule Start';
		const bookingStartActionIcon = bookingStartsNow ? 'fa-play' : 'fa-calendar-plus-o';

		return (
			<div key={lane.pkey || lane.lane_number} className={`live-lane-card ${stateClass}`}>
				<div className="live-lane-status">
					<span className="live-status-left">
						<span className="live-status-dot"/>
						<span>{statusLabel}</span>
					</span>
					<span className={`range-pill ${pillClass}`}>Lane {lane.lane_number || '-'}</span>
				</div>
				<div className="live-lane-heading">
					<div>
						<h5>{lane.name}</h5>
						<div className="lane-meta">{lane.lane_type || 'Standard'} / Capacity {lane.capacity || 1}</div>
					</div>
					<div className="live-lane-number">
						<span>{lane.lane_number || '-'}</span>
					</div>
				</div>

				{booking ? (
					<div className="live-session-card">
						<div className="live-session-main">
							<div>
								<div className="live-session-label">Customer on lane</div>
								{booking.customer ? (
									<a className="live-customer-link" href={`/customers/${booking.customer}`}>
										{booking.customer_name || 'Customer Profile'}
									</a>
								) : (
									<strong>{booking.customer_name || 'Walk-in Customer'}</strong>
								)}
							</div>
							<div className="live-countdown">{remainingLabel}</div>
						</div>
						<div className="live-session-meta">
							<span><i className="fa fa-clock-o"/>&nbsp;{this.formatTimeRange(booking.scheduled_start, booking.scheduled_end)}</span>
							<span><i className="fa fa-users"/>&nbsp;Party {booking.party_size || 1}</span>
							<span>{this.formatCurrency(booking.total_amount || 0)}</span>
						</div>
						<div className="live-progress">
							<span style={{width: `${progress}%`}}/>
						</div>
					</div>
				) : (
					<div className="live-session-card idle">
						<div>
							<div className="live-session-label">Ready for next booking</div>
							<strong>{baseMinutes} min base time</strong>
						</div>
						<div className="live-session-meta">
							<span><i className="fa fa-plus-circle"/>&nbsp;{additionalMinutes} min add-ons</span>
							<span><i className="fa fa-bolt"/>&nbsp;Start or schedule</span>
						</div>
					</div>
				)}

				<div className="live-lane-actions">
					{booking ? (
						<>
							<div className="add-time-action">
								<button
									type="button"
									className="live-action-chip"
									title={addTimeNotice.disabled ? addTimeNotice.disabledReason : addTimeNotice.label}
									disabled={addTimeNotice.disabled || this.state.paymentProcessing}
									onClick={() => this.confirmAddTime(booking)}
								>
									<i className="fa fa-plus"/><span>Add time</span>
								</button>
								<span className={`add-time-notice ${addTimeNotice.disabled ? 'warning' : ''}`}>
									{addTimeNotice.disabled ? addTimeNotice.disabledReason : addTimeNotice.label}
								</span>
							</div>
							<button type="button" className="live-action-chip success" title="End session and keep the booking/payment record" onClick={() => this.runBookingAction('complete', booking)}>
								<i className="fa fa-check"/><span>End Session</span>
							</button>
							{!bookingFinished ? (
								<button type="button" className="live-action-chip danger" title="Cancel booking without refunding payment" onClick={() => this.runBookingAction('cancel', booking)}>
									<i className="fa fa-ban"/><span>Cancel Booking</span>
								</button>
							) : null}
							{Number(booking.refundable_amount || 0) > 0 ? (
								<button type="button" className="live-action-chip danger" title="Refund Range Manager payment" onClick={() => this.refundBooking(booking)}>
									<i className="fa fa-undo"/><span>Refund</span>
								</button>
							) : null}
						</>
					) : (
						<button
							type="button"
							className="live-action-chip primary"
							title={bookingStartsNow ? 'Start lane at the current time' : 'Schedule lane for the selected start time'}
							disabled={lane.status !== 'active' || this.state.paymentProcessing}
							onClick={() => this.createBooking(lane.pkey)}
						>
							<i className={`fa ${bookingStartActionIcon}`}/><span>{this.state.paymentProcessing ? 'Charging' : bookingStartActionLabel}</span>
						</button>
					)}
				</div>
			</div>
		);
	}

	renderLiveView() {
		const activeLanes = this.state.lanes.filter(lane => lane.status === 'active');
		const occupiedCount = this.state.lanes.filter(lane => lane.current_booking).length;
		const availableCount = this.availableLanes().length;
		const utilization = activeLanes.length ? Math.round((occupiedCount / activeLanes.length) * 100) : 0;
		const nextWaitlistEntry = this.state.waitlist[0];
		const waitlistLabel = nextWaitlistEntry ?
			`${nextWaitlistEntry.customer_name || 'Waiting customer'} / party ${nextWaitlistEntry.party_size || 1}` :
			'No waitlist pressure';

		return (
			<div className="live-dashboard-layout">
				<div className="live-workflow-strip">
					{this.renderBookingForm('operations')}
					{this.renderWaitlistPanel('operations')}
				</div>
				<div className="live-ops-panel">
					<div className="live-ops-header">
						<div>
							<div className="live-kicker">
								<span className="live-pulse"/>
								<span>Live range floor</span>
							</div>
							<h4>Lane Operations</h4>
							{this.state.refreshing ? <span className="refresh-note">Refreshing...</span> : null}
						</div>
						<button type="button" className="live-refresh" onClick={this.handleManualRefresh} disabled={this.state.refreshing}>
							<i className="fa fa-refresh"/><span>{this.state.refreshing ? 'Refreshing' : 'Refresh'}</span>
						</button>
					</div>

					<div className="live-ops-strip">
						<div className="live-ops-metric occupied">
							<span>{occupiedCount}</span>
							<label>On lane</label>
						</div>
						<div className="live-ops-metric open">
							<span>{availableCount}</span>
							<label>Ready</label>
						</div>
						<div className="live-ops-metric">
							<span>{this.state.waitlist.length}</span>
							<label>Waiting</label>
						</div>
						<div className="live-ops-meter">
							<div>
								<strong>{utilization}% occupied</strong>
								<span>{waitlistLabel}</span>
							</div>
							<div className="live-meter-track">
								<span style={{width: `${utilization}%`}}/>
							</div>
						</div>
					</div>

					{this.state.lanes.length ? (
						<div className="live-lane-grid">
							{this.state.lanes.map(lane => this.renderLaneBoardLane(lane))}
						</div>
					) : (
						<div className="empty-state">
							Configure lanes before using the live dashboard.
						</div>
					)}
				</div>
			</div>
		);
	}

	renderWaitlistPanel(layout = 'stacked') {
		const lanes = this.availableLanes();
		const waitlistEstimates = this.getWaitlistLaneEstimates();
		const nextEntryEstimate = waitlistEstimates.nextEntry;
		const waitlistPanelOpen = this.state.waitlistPanelOpen;
		const liveOperationsLayout = layout === 'operations';
		const customerMode = this.state.waitlistForm.customer_mode || 'existing';

		return (
			<div className={`panel collapsible-panel waitlist-panel ${liveOperationsLayout ? 'waitlist-panel-operations' : ''} ${waitlistPanelOpen ? 'open' : 'collapsed'}`}>
				<button
					type="button"
					className="collapsible-panel-header"
					onClick={() => this.togglePanel('waitlistPanelOpen')}
					aria-expanded={waitlistPanelOpen}
				>
					<span>
						<span className="collapsible-panel-title">Waitlist</span>
						<span className="collapsible-panel-subtitle">
							{this.state.waitlist.length} waiting / {nextEntryEstimate.label}
						</span>
					</span>
					<span className="collapsible-panel-actions">
						<span className="range-pill">{this.state.waitlist.length}</span>
						<i className={`fa ${waitlistPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}/>
					</span>
				</button>
				{waitlistPanelOpen ? (
					<>
						<div className={`compact-form waitlist-form-grid ${liveOperationsLayout ? 'waitlist-form-grid-operations' : ''}`}>
							<div className="calendar-event compact">
								<div className="calendar-event-title">
									<strong>Next new wait</strong>
									<span className={`range-pill ${nextEntryEstimate.minutes === 0 ? 'green' : 'blue'}`}>
										{nextEntryEstimate.label}
									</span>
								</div>
								<div className="lane-meta">
									{nextEntryEstimate.lane ?
										`${nextEntryEstimate.lane.name} at ${nextEntryEstimate.startsAt.format('h:mm A')}` :
										'No active lane matches the requested lane type.'
									}
								</div>
							</div>
							<div className="booking-customer-field waitlist-customer-field">
								<div className="customer-selector-header">
									<label>Customer</label>
									<div className="customer-mode-toggle">
										<button
											type="button"
											className={customerMode === 'existing' ? 'active' : ''}
											onClick={() => this.setWaitlistCustomerMode('existing')}
										>
											Existing
										</button>
										<button
											type="button"
											className={customerMode === 'new' ? 'active' : ''}
											onClick={() => this.setWaitlistCustomerMode('new')}
										>
											New
										</button>
									</div>
								</div>
								{customerMode === 'existing' ? (
									<Framework.Elements.Select
										shouldLoad={true}
										update={this.updateWaitlistCustomer}
										property={customerSelectProperty}
										value={this.state.waitlistForm.customer}
										error={''}
										object={this.state.waitlistForm.customers}
										text={'name'}
										textHelper={'phone/email'}
										sort={'name'}
										nullable={true}
										default={{value: '', label: 'Select Customer'}}
										showToolTip={true}
									/>
								) : (
									<input
										value={this.state.waitlistForm.customer_name}
										onChange={event => this.updateNewWaitlistCustomer('customer_name', event.target.value)}
										placeholder="Walk-in Customer"
									/>
								)}
							</div>
							<div className="two-col">
								<div>
									<label>Phone</label>
										<input
											inputMode="numeric"
											maxLength="12"
											value={this.state.waitlistForm.customer_phone}
										onChange={event => this.updateNewWaitlistCustomer('customer_phone', event.target.value)}
									/>
								</div>
								<div>
									<label>Party</label>
									<input
										type="number"
										min="1"
										value={this.state.waitlistForm.party_size}
										onChange={event => this.updateForm('waitlistForm', 'party_size', event.target.value)}
									/>
								</div>
							</div>
							<div className="two-col">
								<div>
									<label>Minutes</label>
									<input
										type="number"
										min="1"
										value={this.state.waitlistForm.requested_minutes}
										onChange={event => this.updateForm('waitlistForm', 'requested_minutes', event.target.value)}
									/>
								</div>
								<div>
									<label>Lane Type</label>
									<input
										value={this.state.waitlistForm.requested_lane_type}
										onChange={event => this.updateForm('waitlistForm', 'requested_lane_type', event.target.value)}
										placeholder="Any"
									/>
								</div>
							</div>
							<div>
								<label>Rate</label>
								<select
									value={this.state.waitlistForm.requested_rate_plan}
									onChange={event => this.updateForm('waitlistForm', 'requested_rate_plan', event.target.value)}
								>
									{this.getActiveRatePlans().map(rangeRatePlan => (
										<option key={rangeRatePlan.pkey || rangeRatePlan.name} value={rangeRatePlan.pkey || ''}>
											{rangeRatePlan.name}
										</option>
									))}
								</select>
							</div>
							<Button className="primary-button" size="sm" onClick={this.addWaitlistEntry}>
								<i className="fa fa-plus"/>&nbsp;Add to Waitlist
							</Button>
						</div>
						{this.renderWaitlistPaymentControls()}
						<div className="waitlist-entry-list">
							{this.state.waitlist.length ? this.state.waitlist.map((entry) => {
								const waitlistPreview = this.getWaitlistEntryPreview(entry);
								const paymentPayload = this.getBookingPaymentPayload(waitlistPreview.estimatedTotal);
								const promoteLabel = paymentPayload.payment_method === 'cash' ?
									'Record & Promote' :
									(paymentPayload.charge_with_ipospays ? 'Charge & Promote' : 'Promote');

								return (
									<div key={entry.pkey} className="calendar-event">
										<div className="calendar-event-title">
											<strong>{entry.customer_name}</strong>
											<span className={`range-pill ${waitlistEstimates.entriesByPkey[entry.pkey] && waitlistEstimates.entriesByPkey[entry.pkey].minutes === 0 ? 'green' : 'blue'}`}>
												{waitlistEstimates.entriesByPkey[entry.pkey] ? waitlistEstimates.entriesByPkey[entry.pkey].label : 'Calculating'}
											</span>
										</div>
										<div className="lane-meta">
											Party {entry.party_size} · Joined {moment(entry.joined_at).format('h:mm A')}
										</div>
										<div className="lane-meta">
											{waitlistPreview.rateName} / {waitlistPreview.requestedMinutes} min / {this.formatCurrency(waitlistPreview.estimatedTotal)} est. {waitlistPreview.priceMode}
										</div>
										{waitlistEstimates.entriesByPkey[entry.pkey] && waitlistEstimates.entriesByPkey[entry.pkey].lane ? (
											<div className="lane-meta">
												{waitlistEstimates.entriesByPkey[entry.pkey].lane.name} at {waitlistEstimates.entriesByPkey[entry.pkey].startsAt.format('h:mm A')}
											</div>
										) : null}
										<div className="row-actions">
											<select
												value={this.state.promoteLaneByEntry[entry.pkey] || ''}
												onChange={event => this.setState({
													promoteLaneByEntry: {
														...this.state.promoteLaneByEntry,
														[entry.pkey]: event.target.value,
													},
												})}
											>
												<option value="">Select lane</option>
												{lanes.map(lane => (
													<option key={lane.pkey} value={lane.pkey}>{lane.name}</option>
												))}
											</select>
											<Button size="sm" color="success" disabled={this.state.paymentProcessing} onClick={() => this.promoteWaitlistEntry(entry)}>
												<i className="fa fa-arrow-right"/>&nbsp;{this.state.paymentProcessing ? 'Charging' : promoteLabel}
											</Button>
											<Button size="sm" className="secondary-button" disabled={this.state.paymentProcessing} onClick={() => this.removeWaitlistEntry(entry)}>
												Remove
											</Button>
										</div>
									</div>
								);
							}) : (
								<div className="empty-state">No customers are waiting.</div>
							)}
						</div>
					</>
				) : null}
			</div>
		);
	}

	renderCalendarToolbar() {
		const calendarRange = this.getCalendarRange();
		const calendarViews = [
			{id: 'day', label: 'Daily'},
			{id: 'week', label: 'Weekly'},
			{id: 'month', label: 'Monthly'},
		];

		return (
			<div className="calendar-toolbar">
				<div>
					<label>Calendar Date</label>
					<input
						type="date"
						value={this.state.calendarDate}
						onChange={event => this.setCalendarDate(event.target.value)}
					/>
				</div>
				<div>
					<label>View</label>
					<div className="calendar-view-tabs">
						{calendarViews.map(calendarView => (
							<button
								key={calendarView.id}
								type="button"
								className={this.state.calendarViewMode === calendarView.id ? 'active' : ''}
								onClick={() => this.setCalendarViewMode(calendarView.id)}
							>
								{calendarView.label}
							</button>
						))}
					</div>
				</div>
				<div className="calendar-nav">
					<label>{calendarRange.label}</label>
					<div className="row-actions" style={{marginTop: 0}}>
						<Button className="secondary-button" size="sm" onClick={() => this.shiftCalendarDate(-1)}>
							<i className="fa fa-chevron-left"/>&nbsp;Prev
						</Button>
						<Button className="secondary-button" size="sm" onClick={this.resetCalendarDate}>
							Today
						</Button>
						<Button className="secondary-button" size="sm" onClick={() => this.shiftCalendarDate(1)}>
							Next&nbsp;<i className="fa fa-chevron-right"/>
						</Button>
					</div>
				</div>
				<Button className="secondary-button" size="sm" onClick={this.handleManualRefresh} disabled={this.state.refreshing}>
					<i className="fa fa-refresh"/>&nbsp;{this.state.refreshing ? 'Refreshing' : 'Refresh'}
				</Button>
				<Button className="primary-button" size="sm" onClick={() => this.startBlockForDay(this.state.calendarDate)}>
					<i className="fa fa-plus"/>&nbsp;Block Selected Day
				</Button>
			</div>
		);
	}

	renderCalendarEvent(event, compact = false) {
		const booking = event.type === 'booking' ? event.source : null;
		const block = event.type === 'block' ? event.source : null;
		const eventStatus = booking ? this.getBookingDisplayStatus(booking) : event.status;
		const bookingFinished = eventStatus === 'finished';
		const eventClassName = `calendar-event ${event.type === 'block' ? 'block' : ''} ${compact ? 'compact' : ''}`;
		const eventStatusClass = event.type === 'block' ? 'amber' : (eventStatus === 'cancelled' ? 'red' : (eventStatus === 'completed' || eventStatus === 'finished' ? 'green' : 'blue'));

		return (
			<div
				key={event.key}
				className={`${eventClassName} ${block ? 'editable' : ''}`}
				onClick={(clickEvent) => {
					clickEvent.stopPropagation();
					if (block) {
						this.editBlock(block);
					}
				}}
				role={block ? 'button' : undefined}
				tabIndex={block ? 0 : undefined}
				onKeyDown={block ? (keyEvent) => {
					if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
						keyEvent.preventDefault();
						this.editBlock(block);
					}
				} : undefined}
			>
				<div className="calendar-event-title">
					<strong>{event.title}</strong>
					<span className={`range-pill ${eventStatusClass}`}>{eventStatus}</span>
				</div>
				<div>{this.formatTimeRange(event.start, event.end)}</div>
				<div className="lane-meta">{this.getLaneName(event.lane)}</div>
				{!compact && booking ? (
					<div className="row-actions">
						{booking.status === 'scheduled' || booking.status === 'checked_in' ? (
							<Button className="primary-button" size="sm" onClick={() => this.runBookingAction('start', booking)}>
								Start
							</Button>
						) : null}
						{!bookingFinished ? (
							<Button size="sm" color="danger" onClick={() => this.runBookingAction('cancel', booking)}>
								Cancel
							</Button>
						) : null}
					</div>
				) : null}
				{!compact && block ? (
					<div className="row-actions">
						<Button className="primary-button" size="sm" onClick={(clickEvent) => { clickEvent.stopPropagation(); this.editBlock(block); }}>
							Edit
						</Button>
						<Button size="sm" color="danger" onClick={(clickEvent) => { clickEvent.stopPropagation(); this.deleteBlock(block); }}>
							Remove
						</Button>
					</div>
				) : null}
			</div>
		);
	}

	renderDailyCalendarView(calendarData) {
		const bookingsByLane = this.state.lanes.reduce((carry, lane) => {
			carry[lane.pkey] = calendarData.bookings.filter(booking => booking.lane === lane.pkey);
			return carry;
		}, {});

		return (
			<div className="calendar-grid">
				{this.state.lanes.map(lane => {
					const laneBookings = (bookingsByLane[lane.pkey] || []).map(booking => this.bookingToCalendarEvent(booking));
					const laneBlocks = calendarData.blocks
						.filter(block => !block.lane || block.lane === lane.pkey)
						.map(block => this.blockToCalendarEvent(block));
					const laneEvents = [...laneBookings, ...laneBlocks].sort((left, right) => moment(left.start).valueOf() - moment(right.start).valueOf());

					return (
						<div
							key={lane.pkey || lane.lane_number}
							className="calendar-lane"
							onClick={() => this.startBlockForDay(this.state.calendarDate, lane.pkey)}
						>
							<h5>{lane.name}</h5>
							{laneEvents.length ? laneEvents.map(event => this.renderCalendarEvent(event)) : (
								<div className="calendar-empty">No events.</div>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	renderPeriodCalendarView(calendarData) {
		const calendarRange = this.getCalendarRange();
		const days = [];
		const cursor = calendarRange.start.clone();
		const selectedDate = moment(this.state.calendarDate);

		while (cursor.isSameOrBefore(calendarRange.end, 'day')) {
			days.push(cursor.clone());
			cursor.add(1, 'day');
		}

		return (
			<div className={`calendar-period calendar-period-${this.state.calendarViewMode}`}>
				{days.map(day => {
					const dayEvents = this.getCalendarEventsForDay(day, calendarData);
					const visibleEvents = dayEvents.slice(0, this.state.calendarViewMode === 'month' ? 3 : 6);
					const isOutsideMonth = this.state.calendarViewMode === 'month' && !day.isSame(selectedDate, 'month');
					const dayKey = day.format('YYYY-MM-DD');
					const hiddenEventCount = dayEvents.length - visibleEvents.length;

					return (
						<div
							key={dayKey}
							className={`calendar-day ${day.isSame(moment(), 'day') ? 'today' : ''} ${isOutsideMonth ? 'outside-month' : ''}`}
							onClick={() => this.startBlockForDay(dayKey)}
						>
							<div className="calendar-day-header">
								<strong>{day.format(this.state.calendarViewMode === 'month' ? 'D' : 'ddd D')}</strong>
								<span>{day.format('MMM')}</span>
							</div>
							{visibleEvents.length ? visibleEvents.map(event => this.renderCalendarEvent(event, true)) : (
								<div className="calendar-day-empty">No events</div>
							)}
							{hiddenEventCount > 0 ? (
								<button
									type="button"
									className="calendar-more"
									onClick={(clickEvent) => {
										clickEvent.stopPropagation();
										this.showCalendarDayDetails(dayKey);
									}}
								>
									Show all {dayEvents.length} entries
								</button>
							) : null}
						</div>
					);
				})}
			</div>
		);
	}

	renderCalendarDayDetails(calendarData) {
		if (this.state.calendarViewMode === 'day' || !this.state.calendarExpandedDay) {
			return null;
		}

		const selectedDay = moment(this.state.calendarExpandedDay);
		const dayEvents = this.getCalendarEventsForDay(selectedDay, calendarData);

		if (!selectedDay.isValid() || !dayEvents.length) {
			return null;
		}

		return (
			<div className="calendar-day-details" ref={this.calendarDayDetailsRef}>
				<div className="calendar-day-details-header">
					<div>
						<label>Day Details</label>
						<h5>{selectedDay.format('dddd, MMMM D')}</h5>
					</div>
					<button
						type="button"
						className="calendar-day-details-close"
						title="Close day details"
						onClick={() => this.setState({calendarExpandedDay: ''})}
					>
						<i className="fa fa-times"/>
					</button>
				</div>
				<div className="calendar-day-details-list">
					{dayEvents.map(event => this.renderCalendarEvent(event))}
				</div>
			</div>
		);
	}

	renderCalendarBody() {
		const calendarData = this.getVisibleCalendarData();

		return (
			<>
				{this.state.calendarViewMode === 'day' ?
					this.renderDailyCalendarView(calendarData) :
					this.renderPeriodCalendarView(calendarData)}
				{this.renderCalendarDayDetails(calendarData)}
			</>
		);
	}

	renderCalendarView() {
		return (
			<div>
				<div className="panel">
					{this.renderCalendarToolbar()}
					{this.renderCalendarBody()}
				</div>
				<div className="section-grid">
					{this.renderBookingForm()}
					{this.renderBlockPanel()}
				</div>
			</div>
		);
	}

	renderBlockPanel() {
		return (
			<div className="panel block-panel" ref={this.blockPanelRef}>
				<div className="panel-header">
					<h4>{this.state.blockForm.pkey ? 'Edit Blocked Lane Time' : 'Block Lane Time'}</h4>
				</div>
				<div className="compact-form block-form-grid">
					<div>
						<label>Lane</label>
						<select
							value={this.state.blockForm.lane}
							onChange={event => this.updateForm('blockForm', 'lane', event.target.value)}
						>
							<option value="">All lanes</option>
							{this.state.lanes.map(lane => (
								<option key={lane.pkey || lane.lane_number} value={lane.pkey || ''}>{lane.name}</option>
							))}
						</select>
					</div>
					<div>
						<label>Title</label>
						<input
							value={this.state.blockForm.title}
							onChange={event => this.updateForm('blockForm', 'title', event.target.value)}
							placeholder="Maintenance"
						/>
					</div>
					<div className="block-time-grid">
						<div>
							<label>Starts</label>
							<input
								type="datetime-local"
								value={this.state.blockForm.starts_at}
								onChange={event => this.updateForm('blockForm', 'starts_at', event.target.value)}
							/>
						</div>
						<div>
							<label>Ends</label>
							<input
								type="datetime-local"
								value={this.state.blockForm.ends_at}
								onChange={event => this.updateForm('blockForm', 'ends_at', event.target.value)}
							/>
						</div>
					</div>
					<div>
						<label>Type</label>
						<select
							value={this.state.blockForm.block_type}
							onChange={event => this.updateForm('blockForm', 'block_type', event.target.value)}
						>
							<option value="maintenance">Maintenance</option>
							<option value="private_event">Private Event</option>
							<option value="closure">Closure</option>
							<option value="training">Training</option>
							<option value="other">Other</option>
						</select>
					</div>
					<div>
						<label>Notes</label>
						<textarea
							value={this.state.blockForm.notes}
							onChange={event => this.updateForm('blockForm', 'notes', event.target.value)}
						/>
					</div>
					<div className="block-form-actions">
						{this.state.blockForm.pkey ? (
							<Button className="secondary-button" size="sm" onClick={this.cancelBlockEdit} disabled={this.state.blockSaving}>
								Cancel Edit
							</Button>
						) : null}
						<Button className="primary-button" size="sm" onClick={this.createBlock} disabled={this.state.blockSaving}>
							<i className="fa fa-check"/>&nbsp;{this.state.blockSaving ? 'Saving' : (this.state.blockForm.pkey ? 'Save Changes' : 'Block Time')}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	renderRatePlanCard(ratePlan, index) {
		return (
			<div key={ratePlan.pkey || index} className="rate-plan-card">
				<div className="rate-plan-card-header">
					<div>
						<label>Rate Plan Name</label>
						<input
							value={ratePlan.name || ''}
							onChange={event => this.updateRatePlan(index, 'name', event.target.value)}
							placeholder="Standard Lane Time"
						/>
					</div>
					<div className="rate-plan-flags">
						<label>
							<input
								type="checkbox"
								checked={!!ratePlan.is_default}
								onChange={event => this.updateRatePlan(index, 'is_default', event.target.checked)}
							/>
							Default
						</label>
						<label>
							<input
								type="checkbox"
								checked={!!ratePlan.active}
								onChange={event => this.updateRatePlan(index, 'active', event.target.checked)}
							/>
							Active
						</label>
						<Button
							color="danger"
							size="sm"
							disabled={this.state.ratePlans.length <= 1 || !!this.state.deletingRatePlan || this.state.saving}
							onClick={() => this.deleteRatePlan(ratePlan, index)}
							title={this.state.ratePlans.length <= 1 ? 'At least one rate plan is required.' : ''}
						>
							<i className="fa fa-trash"/>&nbsp;{this.state.deletingRatePlan === (ratePlan.pkey || `new-${index}`) ? 'Deleting' : 'Delete'}
						</Button>
					</div>
				</div>
				<div className="rate-plan-fields">
					<div>
						<label>Base Price</label>
						<div className="money-input">
							<span>$</span>
							<input
								type="number"
								min="0"
								step="0.01"
								value={ratePlan.base_price || ratePlan.base_price === 0 ? ratePlan.base_price : ''}
								onChange={event => this.updateRatePlan(index, 'base_price', event.target.value)}
								placeholder="0.00"
							/>
						</div>
						<div className="muted">Charged for the minimum lane time.</div>
					</div>
					<div>
						<label>Added Time Price</label>
						<div className="money-input">
							<span>$</span>
							<input
								type="number"
								min="0"
								step="0.01"
								value={ratePlan.additional_unit_price || ratePlan.additional_unit_price === 0 ? ratePlan.additional_unit_price : ''}
								onChange={event => this.updateRatePlan(index, 'additional_unit_price', event.target.value)}
								placeholder="0.00"
							/>
						</div>
						<div className="muted">Charged for each added unit of time.</div>
					</div>
					<div>
						<label>Pricing Method</label>
						<select value={ratePlan.pricing_type || 'per_lane'} onChange={event => this.updateRatePlan(index, 'pricing_type', event.target.value)}>
							<option value="per_lane">Per Lane</option>
							<option value="per_shooter">Per Shooter</option>
						</select>
					</div>
					<div>
						<label>Base Time Minutes</label>
						<input
							type="number"
							min="5"
							value={ratePlan.base_unit_minutes || 30}
							onChange={event => this.updateRatePlan(index, 'base_unit_minutes', event.target.value)}
						/>
					</div>
					<div>
						<label>Added Time Minutes</label>
						<input
							type="number"
							min="5"
							value={ratePlan.additional_unit_minutes || ratePlan.unit_minutes || 10}
							onChange={event => this.updateRatePlan(index, 'additional_unit_minutes', event.target.value)}
						/>
					</div>
					<div>
						<label>Max Added Units</label>
						<input
							type="number"
							min="0"
							value={ratePlan.maximum_additional_units || ratePlan.maximum_additional_units === 0 ? ratePlan.maximum_additional_units : ''}
							onChange={event => this.updateRatePlan(index, 'maximum_additional_units', event.target.value)}
							placeholder="No max"
						/>
					</div>
				</div>
			</div>
		);
	}

	renderLocationTaxSummary() {
		const taxRate = this.getLocationTaxRate();
		const locationRateRows = this.getRootLocationRateRows();
		const taxComponents = locationRateRows.length ? locationRateRows : this.getLocationTaxComponents();

		return (
			<div className="location-tax-summary">
				<div>
					<strong>Location Tax</strong>
					<span>
						{taxRate > 0 ? `${this.formatTaxRate(taxRate)} applied to Range Manager bookings` : 'No location tax configured for this location'}
					</span>
				</div>
				{taxComponents.length ? (
					<div className="location-tax-components">
						{taxComponents.map((taxComponent, index) => (
							<span key={`${taxComponent.name || 'Tax'}-${index}`}>
								{taxComponent.name || 'Tax'}: {this.formatTaxRate(taxComponent.rate)}
							</span>
						))}
					</div>
				) : null}
			</div>
		);
	}

	renderConfigurationView() {
		const hasPendingLaneCountChange = this.state.laneCountDirty;
		const shouldHighlightSaveConfiguration = this.state.configurationDirty && !hasPendingLaneCountChange;
		const sortedRatePlans = this.state.ratePlans
			.map((ratePlan, index) => ({ratePlan, index}))
			.sort((firstPlan, secondPlan) => Number(!!secondPlan.ratePlan.is_default) - Number(!!firstPlan.ratePlan.is_default));

		return (
			<div>
				<div className="panel">
					<div className="panel-header">
						<h4>Dashboard Settings</h4>
						<Button
							size="sm"
							className={shouldHighlightSaveConfiguration ? 'save-configuration-button has-unsaved-changes primary-button' : 'save-configuration-button primary-button'}
							disabled={this.state.saving || !!this.state.deletingRatePlan}
							onClick={this.saveConfiguration}
						>
							<i className="fa fa-save"/>&nbsp;Save Configuration
						</Button>
					</div>
					{shouldHighlightSaveConfiguration ? (
						<div className="configuration-unsaved-notice" role="alert">
							<i className="fa fa-exclamation-triangle"/>
							<span>You have unsaved configuration changes. Use Save Configuration before leaving this page.</span>
						</div>
					) : null}
					<div className="two-col">
						<div>
							<label>Dashboard Name</label>
							<input
								value={this.state.settings.dashboard_name || ''}
								onChange={event => this.updateSetting('dashboard_name', event.target.value)}
							/>
						</div>
						<div>
							<label>Lane Count</label>
							<div className="row-actions" style={{marginTop: 0}}>
								<input
									type="number"
									min="0"
									value={this.state.laneCountTarget}
									onChange={event => this.updateLaneCountTarget(event.target.value)}
								/>
								<Button
									color={hasPendingLaneCountChange ? 'primary' : 'secondary'}
									size="sm"
									className={hasPendingLaneCountChange ? 'lane-count-apply-button has-unsaved-changes' : 'lane-count-apply-button'}
									onClick={this.applyLaneCount}
								>
									Apply
								</Button>
							</div>
						</div>
					</div>
					<div className="two-col" style={{marginTop: 10}}>
						<div>
							<label>Default Unit Minutes</label>
							<input
								type="number"
								min="5"
								value={this.state.settings.default_unit_minutes || 30}
								onChange={event => this.updateSetting('default_unit_minutes', event.target.value)}
							/>
						</div>
						<div>
							<label>Minimum Units</label>
							<input
								type="number"
								min="1"
								value={this.state.settings.minimum_units || 1}
								onChange={event => this.updateSetting('minimum_units', event.target.value)}
							/>
						</div>
					</div>
					<div className="two-col" style={{marginTop: 10}}>
						<div>
							<label>Grace Minutes</label>
							<input
								type="number"
								min="0"
								value={this.state.settings.grace_minutes || 0}
								onChange={event => this.updateSetting('grace_minutes', event.target.value)}
							/>
						</div>
						<label style={{alignSelf: 'end'}}>
							<input
								type="checkbox"
								style={{width: 'auto', minHeight: 0, marginRight: 8}}
								checked={!!this.state.settings.allow_lane_extensions}
								onChange={event => this.updateSetting('allow_lane_extensions', event.target.checked)}
							/>
							Allow lane extensions
						</label>
					</div>
					{this.renderLocationTaxSummary()}
				</div>
				<div className="panel">
					<div className="panel-header">
						<h4>Lanes</h4>
					</div>
					<div style={{overflowX: 'auto'}}>
						<table className="config-table">
							<thead>
								<tr>
									<th>Name</th>
									<th>Number</th>
									<th>Type</th>
									<th>Capacity</th>
									<th>Status</th>
									<th>Notes</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{this.state.lanes.map((lane, index) => (
									<tr key={lane.pkey || index}>
										<td><input value={lane.name || ''} onChange={event => this.updateLane(index, 'name', event.target.value)}/></td>
										<td><input type="number" min="1" value={lane.lane_number || index + 1} onChange={event => this.updateLane(index, 'lane_number', event.target.value)}/></td>
										<td><input value={lane.lane_type || ''} onChange={event => this.updateLane(index, 'lane_type', event.target.value)}/></td>
										<td><input type="number" min="1" value={lane.capacity || 1} onChange={event => this.updateLane(index, 'capacity', event.target.value)}/></td>
										<td>
											<select value={lane.status || 'active'} onChange={event => this.updateLane(index, 'status', event.target.value)}>
												<option value="active">Active</option>
												<option value="inactive">Inactive</option>
												<option value="maintenance">Maintenance</option>
											</select>
										</td>
										<td><input value={lane.notes || ''} onChange={event => this.updateLane(index, 'notes', event.target.value)}/></td>
										<td>
											<Button
												color="danger"
												size="sm"
												disabled={!!lane.current_booking}
												onClick={() => this.deleteLane(lane, index)}
											>
												<i className="fa fa-trash"/>&nbsp;Delete
											</Button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
				<div className="panel">
					<div className="panel-header">
						<h4>Rate Plans</h4>
						<Button className="secondary-button" size="sm" onClick={this.addRatePlan}>
							<i className="fa fa-plus"/>&nbsp;Add Rate
						</Button>
					</div>
					<div className="rate-plan-list">
						{sortedRatePlans.map(({ratePlan, index}) => this.renderRatePlanCard(ratePlan, index))}
					</div>
				</div>
			</div>
		);
	}

	renderActiveView() {
		if (this.state.activeView === 'calendar') {
			return this.renderCalendarView();
		}

		if (this.state.activeView === 'configuration') {
			return this.renderConfigurationView();
		}

		return this.renderLiveView();
	}

	renderTerminalPaymentBanner() {
		if (!this.state.paymentProcessing) {
			return null;
		}

		return (
			<div className="terminal-payment-banner" role="status" aria-live="polite">
				<span className="terminal-payment-spinner"/>
				<div className="terminal-payment-copy">
					<strong>Terminal ready for card</strong>
					<span>{this.state.paymentStatusMessage || 'Waiting for the customer to complete the terminal charge.'}</span>
				</div>
				<button
					type="button"
					className="terminal-payment-abort"
					disabled={this.state.paymentAbortProcessing || this.paymentAbortRequested}
					onClick={this.abortTerminalPayment}
				>
					{this.state.paymentAbortProcessing ? 'Aborting' : (this.paymentAbortRequested ? 'Abort sent' : 'Abort')}
				</button>
			</div>
		);
	}

	render() {
		return (
			<div className="container-fluid range-manager">
				<Framework.Components.PageBar breadcrumbs={breadcrumbsStatic} history={this.props.history}/>
				{this.renderTerminalPaymentBanner()}
				<div className="range-header">
					<div>
						<h3>{this.state.settings.dashboard_name || 'Range Manager'}</h3>
						<p>Live lane status, waitlist, range calendar, and lane pricing.</p>
					</div>
					<Button className="secondary-button" size="sm" onClick={this.handleManualRefresh} disabled={this.state.refreshing}>
						<i className="fa fa-refresh"/>&nbsp;{this.state.refreshing ? 'Refreshing' : 'Refresh'}
					</Button>
				</div>
				{this.renderSummary()}
				{this.renderTabs()}
				{this.state.loading ? (
					<div className="panel">
						<Framework.Elements.LoadingSpinner isLoading={true}/>
					</div>
				) : this.renderActiveView()}
			</div>
		);
	}
}

export default Framework.Components.withRootState(RangeManager);
